import { Component, OnInit, OnDestroy, signal, ChangeDetectionStrategy, inject, ViewChild, Input, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Explorer, ContextMenuClickInfo, IAddButtonOptions, ContextMenuItem, ContextMenuComponent, IPaginationReqMessage, openOffCanvas, closeOffCanvas, OffCanvasPlacement, OptimaOffCanvasService, OffCanvasIDsInDefaultLayout, ModalDialogSizes, ToastService, BasePaginationController, Page } from '@optima-stride-ai/optima-components';
import { BizEntityTypeCategory, BizEntityFunctionTypes, IBizEntity, IBizEntityType, LoggerService, IHierarchyItem, IRights } from '@optima-stride-ai/core';
import { ModalModule, ButtonModule, OffcanvasService } from '@coreui/angular';
import { IconDirective } from '@coreui/icons-angular';
import { FileUploadComponent } from '../../components/file-upload/file-upload.component';
import { FileViewerContainerComponent } from './components/file-viewer-container.component';

// Services
import { LibraryMenuService } from './services/library-menu.service';
import { LibraryFacadeService } from './services/library-facade.service';
import { FileOperationsService } from './services/file-operations.service';
import { LibraryExplorerStateService } from './services/library-explorer-state.service';
import { LibraryDataTransformerService } from './services/library-data-transformer.service';
import { LibraryThumbnailEventService } from './services/library-thumbnail-event.service';
import { GlobalStateService } from '../../../service/global.service';
import { AppStateService } from '../../services/state-store/app-state';
import { BizEntityTypeStateStoreService, filterBizEntitiesForApplicationExplorer, FormBizEntityComponent, findAncestors } from '@optima-stride-ai/optima-app-framework';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { ContextMenuService } from '../../context-menu/context-menu.service';
import { FormStates, OffCanvasComponents } from '../../off-canvas/off-canvas-service/off-canvas-form.service';
import { FileSheetFooterService } from '../../components/google-sheet-footer/google-sheet-footer.service';

import { UIActions, BizEntityConstants, Routes, UIMessages } from '../../shared/enums/app-constants';
import { QueryParams, QueryParamValues, BooleanString, SessionStorageKeys, SessionStorageValues } from '../../../enum/query-params';
import { AdditionalFieldNames } from '../../../enum/additional-fields';
import { IBizEntityWithAccessUrl } from './library.service';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { GetDefaultConfig, getExplorerPropertiesBasedOnBizEntityType } from '../../components/modal 2/modal';
import { OptimaDefaultModalService } from '../../components/modal 2/modal.service';
import { EntityFileUrlService } from '../../services/entity-file-url.service';
import { EntityLayoutConfigService } from '../../services/entity-layout-config.service';

/**
 * Refactored Libraries Page Component - Now loosely coupled and maintainable
 * 
 * Responsibilities (Orchestration Only):
 * - Initialize library data and explorer
 * - Coordinate between services
 * - Handle user interactions and delegate to services
 * - Manage component-level state signals
 * 
 * Business logic, data transformation, and file operations are now in dedicated services
 */
@Component({
  selector: 'app-libraries-page',
  imports: [
    Explorer,
    CommonModule,
    ModalModule,
    ButtonModule,
    IconDirective,
    ContextMenuComponent,
    FileUploadComponent,
    FileViewerContainerComponent
  ],
  templateUrl: './libraries-page-refactored.component.html',
  styleUrls: ['./libraries-page.component.sass'],
  host: {
    '(document:click)': 'onDocumentClick($event)'
  }
})
export class LibrariesPageComponent extends BasePaginationController<IBizEntity> implements OnInit, OnDestroy {
  @Input() isInModal: boolean = false; // To determine if component is used inside a modal
  @Input() modalCategory: BizEntityTypeCategory | null = null; // Category filter when used in modal
  @ViewChild('menu') menu!: ContextMenuComponent;
  @ViewChild('svgContextMenu') svgContextMenu!: ContextMenuComponent;
  @ViewChild('fileUpload') fileUploadComponent!: FileUploadComponent;
  @ViewChild('fileViewerContainer') fileViewerContainer?: FileViewerContainerComponent;

  // Inject services
  private readonly menuService = inject(LibraryMenuService);
  private readonly facade = inject(LibraryFacadeService);
  private readonly entityFileUrlService = inject(EntityFileUrlService);
  private readonly entityLayoutConfigService = inject(EntityLayoutConfigService);
  public readonly fileOps = inject(FileOperationsService);
  private readonly modalService = inject(OptimaDefaultModalService);
  private readonly logger = inject(LoggerService);
  private readonly explorerState = inject(LibraryExplorerStateService);
  private readonly dataTransformer = inject(LibraryDataTransformerService);
  private readonly thumbnailEventService = inject(LibraryThumbnailEventService);
  private readonly footerService = inject(FileSheetFooterService);
  private readonly global = inject(GlobalStateService);
  private readonly appStateService = inject(AppStateService);
  private readonly bizEntityTypeStateStore = inject(BizEntityTypeStateStoreService);
  private readonly bizEntityStateStore = inject(BizEntityStateStoreService);
  private readonly contextMenuService = inject(ContextMenuService);
  private readonly offCanvasFormService = inject(OptimaOffCanvasService);
  private readonly offCanvasServiceRef = inject(OffcanvasService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  // Component state
  public mode: IRights = {} as IRights;
  public availableBizEntityTypes = signal<IBizEntityType[]>([]);
  public bizEntityId = '';
  public category = 0;
  public readonly bizEntityTypeCategory = BizEntityTypeCategory;
  public currentFileItem = signal<IBizEntity | null>(null);
  private isAutoOpeningFromQuery = false;
  private isAutoOpeningCreatedFile = false; // Flag to prevent refresh when auto-opening file
  private queryParentId: string | null = null;
  // ID of a structural (folder) entity that was opened early from the dashboard.
  // Stored by tryOpenDashboardFileEarly so initializeLibraryData can preselect it
  // in the Explorer nav and load its children as the initial content view.
  private dashboardStructuralEntityId: string | null = null;
  private queryTypeId: string | null = null;
  private newTemplateCategory: number | null = null;
  // Context menu items for SVG three dots
  public svgContextMenuItems = signal<ContextMenuItem[]>([]);

  // Route tracking
  private routeUrl: string = '';
  private catergory: BizEntityTypeCategory | null = null;
  private subscriptions: Subscription[] = [];
  private libraryEntitiesSnapshot: IBizEntity[] = [];
  private libraryChildrenByParent = new Map<string, IBizEntity[]>();
  private folderCategoryMatchCache = new Map<string, boolean>();
  private folderTemplateMatchCache = new Map<string, boolean>();
  private librarySnapshotComplete = false;
  private snapshotLoadPromise: Promise<void> | null = null;
  // Page size used for content pagination requests, controlled by this consumer.
  public readonly contentItemCountPerPage = 30;

  // Component state flags
  private _isActiveComponent = false;

  // Dialog state
  showUploadDialog = signal<boolean>(false);
  selectedUploadFolder = signal<string | null>(null);
  isOpen = false;
  isDetailViewIconClicked = signal<boolean>(false);

  // Loading state for shimmer effect
  isLoading = signal<boolean>(false);

  // Expose service signals and computed values to template
  explorerAttributes = this.explorerState.explorerAttributes;
  dataRefreshedToken = this.explorerState.dataRefreshedToken;
  availableFolders = this.explorerState.availableFolders;

  // Menu items signal (will be updated by effect)
  menuItems = signal<any[]>([]);
  public readonly onViewerBack = () => {
    void this.handleBackFromPreview();
  };


  constructor() {
    super();
    // Effect to update menu items when viewer state or available types change
    effect(async () => {
      const isViewerActive = this.explorerAttributes().explorerContentAttributes?.isApplicationDerivedContent || false;

      // If a file is open in preview mode, show file menu items (Rename, Save, Share, etc.)
      if (isViewerActive) {
        const currentEntity = this.appStateService.selectedBizEntityForEdit || undefined;
        // Use async version to get proper Discussion disabled state
        const items = await this.menuService.buildFileMenuItemsAsync(
          () => this.handleBackFromPreview(),
          (entity) => this.handlePinToDashboard(entity),
          currentEntity,
          (entity) => this.handleSaveAsTemplate(entity),
          (entity) => this.handleSaveFromPreview(entity)
        );
        this.menuItems.set(items);
      } else {
        // Otherwise, show the regular menu items for creating files/folders
        const items = this.menuService.buildMenuItems(
          this.availableBizEntityTypes(),
          (item, event) => this.handleFileUpload(item, event),
          (item, event) => this.handleNewFileCreationByOffCanvas(item, event),
          (item, event) => this.handleCreateFolderFromMenu(item, event),
          (item, category, event) => this.handleNewFromTemplate(item, category, event)
        );
        this.menuItems.set(items);
      }
    });
  }

  /**
   * Keep a local snapshot/index of library entities so category checks can
   * traverse hierarchy without issuing repeated API calls.
   */
  private setLibraryEntitiesSnapshot(
    entities: IBizEntity[] | null | undefined,
    isComplete: boolean = true
  ): void {
    this.libraryEntitiesSnapshot = entities || [];
    this.librarySnapshotComplete = isComplete;
    this.libraryChildrenByParent.clear();
    this.folderCategoryMatchCache.clear();
    this.folderTemplateMatchCache.clear();

    for (const entity of this.libraryEntitiesSnapshot) {
      const parentId = entity.parentId || '';
      const siblings = this.libraryChildrenByParent.get(parentId) || [];
      siblings.push(entity);
      this.libraryChildrenByParent.set(parentId, siblings);
    }
  }

  private async ensureLibrarySnapshotLoaded(): Promise<void> {
    if (this.libraryChildrenByParent.size > 0) {
      return;
    }

    if (!this.snapshotLoadPromise) {
      this.snapshotLoadPromise = (async () => {
        try {
          const allEntities = await this.facade.getAllBizEntities();
          this.setLibraryEntitiesSnapshot(allEntities || [], true);
        } finally {
          this.snapshotLoadPromise = null;
        }
      })();
    }

    await this.snapshotLoadPromise;
  }

  getContentTotalCount(): number {
    const parentId = this.explorerAttributes().explorerContentAttributes?.currentSelectedItem || '';
    return this.getPaginationTotalCount(
      parentId,
      this.explorerAttributes().explorerContentAttributes?.data?.length ?? 0
    );
  }

  /**
   * Cheaply checks each newly-loaded structural (folder) entity for whether it has
   * at least one STRUCTURAL child (the nav tree only ever shows folders, not files),
   * without fetching its full children list. Lets the nav tree hide the expand
   * chevron for confirmed-empty folders one level ahead of a click, regardless of
   * how many children — structural or not — a folder would otherwise contain.
   */
  private async probeStructuralChildrenForEmptiness(entities: IBizEntity[]): Promise<string[]> {
    const structuralFolders = entities.filter((entity) => entity.type?.functionType === BizEntityFunctionTypes.Structural);
    if (structuralFolders.length === 0) {
      return [];
    }

    const structuralCategories = this.availableBizEntityTypes()
      .filter((type) => type.functionType === BizEntityFunctionTypes.Structural)
      .map((type) => type.category);
    // Fall back to an unfiltered probe if entity types haven't loaded yet —
    // passing an empty category array would incorrectly match nothing.
    const categoryFilter = structuralCategories.length > 0 ? structuralCategories : undefined;

    const results = await Promise.all(
      structuralFolders.map(async (folder) => {
        try {
          const page = await this.facade.getBizEntityPage(folder.id, 1, 0, categoryFilter);
          return page.items.length === 0 ? folder.id : null;
        } catch (error) {
          this.logger.warn(`⚠️ Unable to probe children for folder ${folder.id}: ${error}`);
          return null;
        }
      })
    );

    return results.filter((id): id is string => id !== null);
  }

  protected resolvePaginationParentId(request: IPaginationReqMessage): string {
    return request.parentId || this.explorerState.getSelectedParentId() || this.explorerAttributes().explorerContentAttributes?.currentSelectedItem || '';
  }

  protected fetchPaginationPage(parentId: string | undefined, limit: number, offset: number): Promise<Page<IBizEntity>> {
    return this.facade.getBizEntityPage(parentId, limit, offset);
  }

  protected override setPaginationLoading(isLoading: boolean): void {
    this.isLoading.set(isLoading);
  }

  protected override logPaginationDebug(bufferKey: string, appendedItems: IBizEntity[], totalCount: number, itemCountPerPage: number): void {
    const loadedPages = this.paginationBuffer.getLoadedPageCount(bufferKey, itemCountPerPage);
    const remainingPages = this.paginationBuffer.getRemainingPageCount(bufferKey, itemCountPerPage);
    console.log(`[HayagrivaPagination] loadedPages=${loadedPages}, remainingPages=${remainingPages}, items=${appendedItems.length}, totalCount=${totalCount}, loading=false`);
  }

  protected override async resolveKnownEmptyFolderIds(bufferKey: string, appendedItems: IBizEntity[], nextItems: IBizEntity[]): Promise<string[]> {
    const knownEmptyFolderIds = await this.probeStructuralChildrenForEmptiness(nextItems);
    // The folder that was just expanded has no structural children of its own
    // if none of the entities we just fetched for it are structural.
    const expandedHasNoStructuralChildren = bufferKey
      && !appendedItems.some((item) => item.type?.functionType === BizEntityFunctionTypes.Structural);
    if (expandedHasNoStructuralChildren) {
      knownEmptyFolderIds.push(bufferKey);
    }
    return knownEmptyFolderIds;
  }

  protected onNavPaginationChildrenLoaded(parentId: string, appendedItems: IBizEntity[], knownEmptyFolderIds: string[]): void {
    this.explorerState.updateNavigationWithChildren(parentId, appendedItems, knownEmptyFolderIds);
  }

  protected async onContentPaginationChildrenLoaded(parentId: string, appendedItems: IBizEntity[]): Promise<void> {
    await this.explorerState.updateExplorerWithChildren(
      parentId,
      appendedItems,
      (info) => this.onContextMenuClicked(info),
      (id) => this.onItemSelected(id),
      (id) => this.onListItemClicked(id),
      (entity) => this.handleItemDoubleClick(entity)
    );
  }

  protected override onPaginationError(error: unknown): void {
    this.logger.error(`Unable to load the next library page: ${error}`);
  }

  private openAndHydrateContextMenuForEntity(entity: IBizEntity): void {
    // Set entity in app state BEFORE building menu so commands reference the correct entity.
    this.appStateService.selectedBizEntityForEdit = entity;

    // Show sync menu immediately.
    this.explorerAttributes().contextMenuItems = this.contextMenuService.getMenu(entity);

    // Replace with async menu only if this request is still the latest.
    this.contextMenuService.getMenuAsync(entity, true)
      .then((enhancedItems) => {
        const selectedEntityId = this.appStateService.selectedBizEntityForEdit?.id?.toString();
        if (selectedEntityId !== entity.id?.toString()) {
          return;
        }
        this.explorerAttributes().contextMenuItems = enhancedItems;
      })
      .catch((error) => {
        this.logger.error(`Error enhancing context menu with discussion: ${error}`);
      });
  }

  private folderContainsCategoryFilesInSnapshot(
    folderId: string,
    category: BizEntityTypeCategory,
    visited: Set<string> = new Set()
  ): boolean {
    if (!folderId || visited.has(folderId)) {
      return false;
    }
    visited.add(folderId);

    const children = this.libraryChildrenByParent.get(folderId) || [];
    if (!children.length) {
      return false;
    }

    for (const child of children) {
      if (child.type?.category === category) {
        return true;
      }
    }

    for (const child of children) {
      if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
        const hasMatchingContent = this.folderContainsCategoryFilesInSnapshot(child.id, category, visited);
        if (hasMatchingContent) {
          return true;
        }
      }
    }

    return false;
  }

  private folderContainsTemplateFilesInSnapshot(
    folderId: string,
    visited: Set<string> = new Set()
  ): boolean {
    if (!folderId || visited.has(folderId)) {
      return false;
    }
    visited.add(folderId);

    const children = this.libraryChildrenByParent.get(folderId) || [];
    if (!children.length) {
      return false;
    }

    for (const child of children) {
      if (
        child.type?.functionType === BizEntityFunctionTypes.Operational &&
        this.isTemplateEntity(child)
      ) {
        return true;
      }
    }

    for (const child of children) {
      if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
        const hasMatchingContent = this.folderContainsTemplateFilesInSnapshot(child.id, visited);
        if (hasMatchingContent) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * Handle folder/library creation from FAB menu
   */
  private handleCreateFolderFromMenu(item: IBizEntity | any, event: any): void {
    this.logger.info(`🔧 handleCreateFolderFromMenu called for ${item?.name || 'unknown'}`);

    if (item.category === BizEntityTypeCategory.HayagrivaLibrary) {
      // Create Library Folder (root level)
      this.logger.info('🔧 Creating Library Folder');
      openOffCanvas(
        this.offCanvasFormService,
        this.offCanvasServiceRef,
        OffCanvasComponents.AddLibraryFolder,
        OffCanvasIDsInDefaultLayout.l1_id,
        1
      );
    } else {
      // Create regular Folder
      this.logger.info('🔧 Creating regular Folder');
      openOffCanvas(
        this.offCanvasFormService,
        this.offCanvasServiceRef,
        OffCanvasComponents.AddFolder,
        OffCanvasIDsInDefaultLayout.l1_id,
        1
      );
    }
  }

  async ngOnInit(): Promise<void> {
    // Mark component as active FIRST (for incoming selection handler)
    // This must be before any subscriptions are set up
    this._isActiveComponent = true;

    // Ensure viewer state is reset on component initialization
    this.bizEntityId = '';
    this.category = 0;
    this.isOpen = false;

    // Reset the shared explorer state up-front. The state service is
    // `providedIn: 'root'`, so when Angular destroys the previous
    // `/documents/<type>` instance and creates a new one for the next
    // route, the new instance's first render would otherwise inherit the
    // previous route's nav hierarchy. The underlying Explorer Nav child
    // never clears that stale hierarchy when later given empty data (its
    // `prepareData()` early-returns on empty), so we must hand it a clean
    // slate BEFORE its initial render. The async data load below then
    // populates it with the correct route-specific content.
    this.explorerState.resetExplorerState();

    // If modalCategory is provided (when used in modal), set it
    if (this.modalCategory !== null) {
      this.catergory = this.modalCategory;
      this.logger.info(`📋 Modal category set: ${this.modalCategory}`);
    }

    // Open the dashboard-pinned file immediately — before the full Explorer
    // initialisation — so the viewer appears without waiting for getAllBizEntities.
    // initializeLibraryData (below) will restore the viewer state after it
    // reinitialises explorerAttributes.
    if (!this.isInModal) {
      await this.tryOpenDashboardFileEarly();
    }

    // Subscribe to global state changes
    this.setupGlobalSubscriptions();

    // Subscribe to thumbnail menu events
    this.setupThumbnailMenuSubscription();

    // Load entity type data
    await this.loadBizEntityTypes();

    // Store initial route
    this.routeUrl = this.router.url;

    // If in modal with a category, initialize with category data
    if (this.isInModal && this.catergory !== null) {
      this.logger.info(`📋 Modal mode: Initializing with category ${this.catergory}`);
      await this.initializeWithCategoryData(this.catergory);
    } else {
      // Check if we're on a category-specific route
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);

      if (isCategoryRoute) {
        // For category routes, initialize with category-specific data directly
        await this.initializeWithCategoryData();
      } else {
        // For general library route, initialize with all data
        await this.initializeLibraryData();
      }
    }

    // Handle query parameters for item selection (skip in modal mode)
    if (!this.isInModal) {
      this.setupQueryParamHandling();
    }

    // Handle incoming selection from app state (skip in modal mode)
    if (!this.isInModal) {
      this.setupIncomingSelectionHandler();
    }

    // Setup route change monitoring (skip in modal mode)
    if (!this.isInModal) {
      this.setupRouteMonitoring();
    }

    // Check for addnew query parameter and trigger FAB button (skip in modal mode)
    if (!this.isInModal) {
      this.checkForAddNewQueryParam();
    }
  }

  ngOnDestroy(): void {
    // Mark component as inactive
    this._isActiveComponent = false;

    // Cleanup subscriptions
    this.subscriptions.forEach(sub => {
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    });
    this.subscriptions = [];
  }

  /**
   * Setup subscriptions to global state changes
   */
  private setupGlobalSubscriptions(): void {
    this.logger.info('🔔 Setting up global subscriptions');

    // Subscribe to created file data to auto-open the file
    this.subscriptions.push(
      this.global.createdFileData$.subscribe(async (data) => {
        if (data) {
          this.logger.info(`� Auto-opening created file: ${data.file?.name || 'unknown'}`);
          // Set flag to prevent refresh from closing the viewer
          this.isAutoOpeningCreatedFile = true;
          await this.openCreatedFile(data.file, data.category);
          // Clear the data after using it
          this.global.clearCreatedFileData();
          // Reset flag after a short delay
          setTimeout(() => {
            this.isAutoOpeningCreatedFile = false;
          }, 1000);
        }
      })
    );

    this.subscriptions.push(
      this.global.isBizEntityCreated$.subscribe((isCreated) => {
        this.logger.info(`🔔 isBizEntityCreated$ triggered: ${isCreated}`);
        if (isCreated) {
          // Reset the flag immediately to allow future triggers
          this.global.setBizEntityCreated(false);

          // Skip refresh if we're auto-opening a file
          if (!this.isAutoOpeningCreatedFile) {
            // Use safe update pattern (same as file uploads) to preserve parent context
            const currentParentId = this.explorerState.getSelectedParentId();
            if (currentParentId) {
              this.refreshListAfterUpdate(currentParentId);
            } else {
              this.refreshCurrentFolder();
            }
          }
        }
      })
    );

    this.subscriptions.push(
      this.global.isBizEntityUpdated$.subscribe(({ updated, entity }) => {
        this.logger.info(`🔄 isBizEntityUpdated$ triggered: ${updated}, entity: ${entity?.name || 'unknown'}`);
        if (updated) {
          // Reset the flag immediately
          this.global.setBizEntityUpdated(false);

          // Check if the updated entity is Structural (folder/library) or Operational (file)
          const isStructural = entity?.type?.functionType === BizEntityFunctionTypes.Structural;
          this.logger.info(`🔄 Entity type: ${entity?.type?.name || 'unknown'}, functionType: ${entity?.type?.functionType}, isStructural: ${isStructural}`);
          this.logger.info(`🔄 BizEntityFunctionTypes.Structural = ${BizEntityFunctionTypes.Structural}, BizEntityFunctionTypes.Operational = ${BizEntityFunctionTypes.Operational}`);

          if (isStructural) {
            // For structural entities (folders), refresh navigation tree while maintaining current context
            this.logger.info('🔄 Structural entity updated, refreshing navigation tree');
            this.refreshNavigationAfterStructuralUpdate();
          } else {
            // For operational entities (files), just refresh the current list to update the item
            // This maintains the user's current navigation context
            this.logger.info('🔄 Operational entity updated, updating list only');
            const currentParentId = this.explorerState.getSelectedParentId();
            this.logger.info(`🔄 Refreshing list for parent: ${currentParentId}`);
            this.refreshListAfterUpdate(currentParentId);
          }
        }
      })
    );
    this.subscriptions.push(
      this.global.isBizEntityDeleted$.subscribe(({ deleted, entity }) => {
        this.logger.info(`🗑️ isBizEntityDeleted$ triggered: ${deleted}, entity: ${entity?.name || 'unknown'}`);
        if (deleted) {
          // Reset the flag immediately
          this.global.setBizEntityDeleted(false);

          // Check if the deleted entity is Structural (folder/library) or not
          const isStructural = entity?.type?.functionType === BizEntityFunctionTypes.Structural;
          this.logger.info(`🗑️ Entity type: ${entity?.type?.name || 'unknown'}, isStructural: ${isStructural}`);

          if (isStructural) {
            this.logger.info('🗑️ Structural entity deleted, performing full refresh');
            this.refreshCurrentFolder();
          } else {
            this.logger.info('🗑️ Non-structural entity deleted, updating list only');
            // Only refresh the current list, don't change navigation
            const currentParentId = this.explorerState.getSelectedParentId();
            this.logger.info(`🗑️ Refreshing list for parent: ${currentParentId}`);

            // Pass the deleted entity ID to ensure it's filtered out
            const deletedEntityId = entity?.id;
            this.refreshListAfterDelete(currentParentId, deletedEntityId);
          }
        }
      })
    );

    this.subscriptions.push(
      this.global.isRenameClicked$.subscribe((isRenamed) => {
        this.logger.info(`🔔 isRenameClicked$ triggered: ${isRenamed}`);
        if (isRenamed) {
          // Reset the flag immediately
          this.global.setRenameClicked(false);
          openOffCanvas(
            this.offCanvasFormService,
            this.offCanvasServiceRef,
            OffCanvasComponents.UpdateLibraryFolder,
            OffCanvasIDsInDefaultLayout.l1_id,
            1
          );
        }
      })
    );

    // NEW LOGIC: Opening file in viewer (like double-click behavior)
    this.subscriptions.push(
      this.global.isPropertiesOpenClicked$.subscribe((isOpened) => {
        if (isOpened) {
          // Get the currently selected entity from app state
          const selectedEntity = this.appStateService.selectedBizEntityForEdit;
          if (selectedEntity?.type?.functionType === BizEntityFunctionTypes.Structural) {
            const selectedItemId = selectedEntity?.id || this.explorerState.getSelectedParentId() || '';
            if (selectedItemId) {
              this.onItemSelected(selectedItemId);
            }
          }

          if (selectedEntity) {
            // Use the same logic as double-click to open in viewer
            this.handleItemDoubleClick(selectedEntity);
          } else {
            this.logger.warn('⚠️ No item selected to open in viewer');
          }
        }
      })
    );

    // Handle "Open" context menu click
    this.subscriptions.push(
      this.global.isOpenEntityClicked$.subscribe(({ open, entity }) => {
        if (open) {
          // Reset the flag
          this.global.setOpenEntityClicked(false);

          const entityToOpen = entity || this.appStateService.selectedBizEntityForEdit;
          this.logger.info(`🔔 Open clicked from context menu - Entity: ${entityToOpen?.name || 'unknown'}`);

          if (entityToOpen) {
            this.logger.info(`📂 Opening entity from context menu: ${entityToOpen.name}`);

            // Check if it's a structural entity (folder)
            if (entityToOpen.type?.functionType === BizEntityFunctionTypes.Structural) {
              // Navigate into the folder
              this.onItemSelected(entityToOpen.id);
            } else {
              // Open file in viewer (like double-click)
              this.handleItemDoubleClick(entityToOpen);
            }
          } else {
            this.logger.warn('⚠️ No entity to open');
          }
        }
      })
    );

    // Handle Library Folder creation request
    this.subscriptions.push(
      this.global.isLibraryOpen$.subscribe((isLibraryOpen) => {
        if (isLibraryOpen) {
          this.logger.info('📚 Library folder creation requested');
          // Reset the flag immediately to prevent re-triggering
          this.global.setIsLibraryOpen(false);

          // Determine if we're in library context
          const selectedEntity = this.appStateService.selectedBizEntityForEdit;
          const isLibraryContext = selectedEntity?.type?.category === BizEntityTypeCategory.Tenant;

          // if (isLibraryContext) {
          openOffCanvas(
            this.offCanvasFormService,
            this.offCanvasServiceRef,
            OffCanvasComponents.AddLibraryFolder,
            OffCanvasIDsInDefaultLayout.l1_id,
            1
          );
          // }
        }
      })
    );

    // Handle Edit entity request — unified path for ALL biz entity types
    // (library, folder, document, team, role, deliverable, etc.). Uses the
    // framework's generic FormBizEntityComponent so relationships, additional
    // fields, parent/owner, etc. are rendered consistently.
    //
    // Why not split by type: opening a second/custom form on top of an existing
    // one was producing the overlap (e.g. "Add Library Folder" + entity edit
    // form stacked in the same drawer). One path, one form, no overlap.
    this.subscriptions.push(
      this.global.isEditEntityOpen$.subscribe(async (isEditOpen) => {
        if (!isEditOpen) {
          return;
        }
        this.logger.info('✏️ Edit entity requested');
        // Reset the flag immediately to prevent re-triggering
        this.global.setIsEditEntityOpen(false);

        const selected = this.appStateService.selectedBizEntityForEdit;
        if (!selected?.id) {
          this.logger.warn('⚠️ No entity selected for edit');
          return;
        }

        await this.openEditEntityOffCanvas(selected);
      })
    );

    // Handle regular Folder creation request
    this.subscriptions.push(
      this.global.isFolderOpen$.subscribe((isFolderOpen) => {
        if (isFolderOpen) {
          this.logger.info('📁 Folder creation requested');
          // Reset the flag immediately to prevent re-triggering
          this.global.setIsFolderOpen(false);

          openOffCanvas(
            this.offCanvasFormService,
            this.offCanvasServiceRef,
            OffCanvasComponents.AddFolder,
            OffCanvasIDsInDefaultLayout.l1_id,
            1
          );
        }
      })
    );

    // Handle all context-menu actions from a single generic pipeline.
    this.subscriptions.push(
      this.global.menuActionRequested$.subscribe((request) => {
        if (!request) {
          return;
        }

        switch (request.action) {
          case 'upload':
            this.logger.info('📤 File upload requested from context menu');
            this.handleFileUpload(request.item, request.event);
            break;
          case 'new-file':
            this.logger.info(`📄 New file creation requested from context menu for: ${request.item?.name || 'unknown'}`);
            this.handleNewFileCreationByOffCanvas(request.item, request.event);
            break;
          case 'new-from-template':
            this.logger.info(`📋 New from template requested from context menu for category: ${request.category}`);
            this.handleNewFromTemplate(request.item, request.category as BizEntityTypeCategory, request.event);
            break;
          default:
            this.logger.warn(`Unsupported context menu action received: ${request.action}`);
            break;
        }
      })
    );
  }

  /**
   * Close any off-canvas currently open at the given level so callers can
   * push a new form without stacking two drawers on top of each other.
   *
   * Reusable across any flow that opens an off-canvas at a fixed level
   * (edit, add, save-as-template, etc.).
   */
  private async closeExistingOffCanvasAtLevel(level: number): Promise<void> {
    const existing = this.offCanvasFormService.getLastOffCanvasAtLevel(level);
    if (!existing) {
      return;
    }
    try {
      closeOffCanvas(
        this.offCanvasFormService,
        this.offCanvasServiceRef,
        OffCanvasPlacement.End,
        !!existing.componentIsABizEntity
      );
      // Let the off-canvas finish its close animation / state cleanup
      // before the next state is pushed.
      await new Promise((resolve) => setTimeout(resolve, 150));
    } catch (err) {
      this.logger.warn(`Failed to close existing off-canvas at level ${level}: ${err}`);
    }
  }

  /**
   * Hydrate a (possibly lightweight) biz entity from the state store so the
   * generic FormBizEntityComponent can render relationships, additionalInfo,
   * rights, etc. correctly.
   *
   * Only swaps in the fresh copy if it actually has `type` populated —
   * without `type` the framework falls back to "Form Layout Not Provided".
   */
  private async hydrateEntityForEdit(selected: IBizEntity): Promise<IBizEntity> {
    try {
      const all = await this.bizEntityStateStore.getState();
      const fresh = all?.find((e: IBizEntity) => e.id === selected.id);
      if (fresh && fresh.type) {
        return {
          ...fresh,
          type: fresh.type ?? selected.type,
        } as IBizEntity;
      }
    } catch (err) {
      this.logger.warn(`Edit: could not hydrate entity from store: ${err}`);
    }
    return selected;
  }

  /**
   * Open the unified biz-entity edit off-canvas for the given entity.
   *
   * This is the single entry point for editing ANY biz entity type
   * (library, folder, document, team, role, deliverable, etc.). Callers
   * should prefer this over opening type-specific forms so we never stack
   * two edit forms in the same drawer.
   */
  private async openEditEntityOffCanvas(selected: IBizEntity): Promise<void> {
    // Close any off-canvas that's already open at level 1 so we never
    // stack two edit forms in the same drawer.
    await this.closeExistingOffCanvasAtLevel(1);

    // Re-fetch the entity from the store so relationships, additionalInfo,
    // rights, etc. are fully hydrated. The object cached on
    // `selectedBizEntityForEdit` may be a lightweight projection from the
    // explorer list and miss relationship data.
    const entityToEdit = await this.hydrateEntityForEdit(selected);

    // Mark form state so the off-canvas auto-resets on close.
    if (this.appStateService.entityFormState !== FormStates.Idle) {
      this.appStateService.entityFormState = FormStates.Idle;
    }
    this.appStateService.entityFormState = FormStates.Edit;

    // Generic biz-entity edit form (contract: { bizEntity, isEditing }).
    this.offCanvasFormService.dataOfCurrentFormInView = {
      bizEntity: entityToEdit,
      isEditing: true
    };

    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      FormBizEntityComponent,
      OffCanvasIDsInDefaultLayout.l1_id,
      1,
      true // compIsABizEntity → off-canvas resets entityFormState on close
    );
  }

  /**
   * Setup subscription to thumbnail menu events
   */
  private setupThumbnailMenuSubscription(): void {
    this.logger.info('📋 Setting up thumbnail menu subscription');

    // Subscribe to thumbnail SVG three dots menu events
    this.subscriptions.push(
      this.thumbnailEventService.menuRequested$.subscribe(({ event, entity }) => {
        this.logger.info(`📋 Menu requested for entity: ${entity.name}`);

        // Update the app state with the selected entity
        this.appStateService.selectedBizEntityForEdit = entity;

        // Get context menu items - pass the entity with rights
        const menuItems = this.contextMenuService.getMenu(entity);
        this.logger.info(`📋 Got ${menuItems.length} menu items`);

        // Update SVG context menu items
        this.svgContextMenuItems.set(menuItems);

        // Show the SVG context menu
        setTimeout(() => {
          if (this.svgContextMenu) {
            this.svgContextMenu.showContextMenu(event);
            this.logger.info('✅ Context menu displayed for SVG three dots');
          }
        }, 0);
      })
    );

    // Subscribe to footer service context menu clicks (from footerCaption actions)
    this.subscriptions.push(
      this.footerService.contextMenuClick$.subscribe(({ entity, event }) => {
        this.logger.info(`📱 Library received context menu click for: ${entity.name}, Event type: ${event.type}`);

        // Update the app state with the selected entity
        this.appStateService.selectedBizEntityForEdit = entity;

        // Get context menu items - pass the entity with rights
        const menuItems = this.contextMenuService.getMenu(entity);
        this.logger.info(`📋 Got ${menuItems.length} menu items`);

        // Update SVG context menu items
        this.svgContextMenuItems.set(menuItems);

        // Show the SVG context menu
        setTimeout(() => {
          if (this.svgContextMenu) {
            this.svgContextMenu.showContextMenu(event);
            this.logger.info('✅ Context menu displayed from footer action');
          }
        }, 0);
      })
    );
  }

  /**
   * Load BizEntity types from API
   */
  private async loadBizEntityTypes(): Promise<void> {
    try {
      const response = await this.bizEntityTypeStateStore.getState();
      // const filteredData = response?.filter((element) => {
      //   return element.isSystemBasic == false &&
      //     BizEntityTypeCategory.Tenant !== element.category;
      // }) || [];
      this.availableBizEntityTypes.set(response || []);
    } catch (error) {
      this.logger.error(`❌ Error loading entity types: ${error}`);
    }
  }

  /**
   * Initialize library data and explorer
   */
  private async initializeLibraryData(): Promise<void> {
    this.isLoading.set(true);
    // Pagination made this fetch much faster (small first page vs. the whole
    // library), which could resolve before the browser ever paints the shimmer.
    // Guarantee a minimum visible duration so the loading state is never invisible.
    const loadStartedAt = Date.now();
    const MIN_SHIMMER_DURATION_MS = 400;
    // Hoisted so the finally block can read them.
    let viewerWasOpen = false;
    let preOpenParentId = '';
    try {
      const navigationPage = await this.facade.getBizEntityPage(
        undefined,
        20,
        0,
        [
          BizEntityTypeCategory.Tenant,
          BizEntityTypeCategory.ApplicationRootNode,
        ]
      );
      const libraryData = navigationPage.items;
      const currentParentId = this.explorerState.getSelectedParentId();
      const selectedRootId = libraryData.some((entity) => entity.id === currentParentId)
        ? currentParentId
        : libraryData[0]?.id || '';
      const firstContentPage = selectedRootId
        ? await this.facade.getBizEntityPage(selectedRootId, 20, 0)
        : { items: [], totalCount: 0 };
      this.paginationBuffer.setInitialPage(selectedRootId, firstContentPage);
      const knownEmptyFolderIds = await this.probeStructuralChildrenForEmptiness(firstContentPage.items);
      const navigationData = [...libraryData];
      const navigationIds = new Set(navigationData.map((entity) => entity.id));
      for (const entity of firstContentPage.items) {
        if (!navigationIds.has(entity.id)) {
          navigationData.push(entity);
          navigationIds.add(entity.id);
        }
      }
      this.setLibraryEntitiesSnapshot(navigationData, false);
      const currentUrl = this.router.url;
      const params = new URLSearchParams(currentUrl.split('?')[1] || '');
      const rawItemParam = params.get('item') || params.get('selectedItem');
      const itemParam = this.normalizeItemParam(rawItemParam);
      let filteredLibraryData = filterBizEntitiesForApplicationExplorer(navigationData);
      // Filter out templates when not on the templates route
      if (this.routeUrl !== Routes.DOCUMENTS_TEMPLATES) {
        filteredLibraryData = filteredLibraryData.filter((entity) =>
          !entity.additionalInfo?.some((info) =>
            info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
            info.value?.toLowerCase() === BooleanString.TRUE
          )
        );
      }

      const isDashboardSource = sessionStorage.getItem(SessionStorageKeys.DASHBOARD_NAVIGATION_SOURCE) === SessionStorageValues.DASHBOARD;

      // For dashboard navigation:
      // - If a structural entity (folder) was opened early, pass its ID as the preSelected ID
      //   so the Explorer nav highlights it and its children are shown as the initial content.
      //   This avoids extra loading cycles — the correct folder view is built in one pass.
      // - If an operational entity (file) was opened early (viewer is open), pass empty string
      //   so the Explorer does NOT call navigateToPreSelectedItem → onItemSelected, which would
      //   close the viewer. The parent context was already set by tryOpenDashboardFileEarly;
      //   we restore the viewer state in the finally block below.
      // For non-dashboard use itemParam (e.g. a direct ?item= URL) so the nav
      // auto-navigates to the correct folder.
      let explorerPreSelectedId: string;
      if (isDashboardSource && this.dashboardStructuralEntityId) {
        // Structural entity: preselect the folder directly.
        explorerPreSelectedId = this.dashboardStructuralEntityId;
      } else if (isDashboardSource) {
        // Operational entity (file in viewer): don't preselect to avoid closing the viewer.
        explorerPreSelectedId = '';
      } else {
        explorerPreSelectedId = selectedRootId || itemParam || currentParentId;
      }

      // Capture viewer state BEFORE initializeExplorer. The call resets the whole
      // explorerAttributes signal (including isApplicationDerivedContent), so if
      // tryOpenDashboardFileEarly already opened the viewer we must restore it after.
      viewerWasOpen = this.explorerAttributes().explorerContentAttributes?.isApplicationDerivedContent || false;
      preOpenParentId = viewerWasOpen ? this.explorerState.getSelectedParentId() : '';

      await this.explorerState.initializeExplorer(
        filteredLibraryData,
        (item) => this.facade.getContextMenuItems(item),
        (info) => this.onContextMenuClicked(info),
        (itemId) => this.onItemSelected(itemId),
        (itemId) => this.onListItemClicked(itemId),
        (entity) => this.handleItemDoubleClick(entity),
        explorerPreSelectedId,
        true,
        firstContentPage.items,
        knownEmptyFolderIds
      );

      // Set initial selected parent
      const firstItem = filteredLibraryData.find((item: any) =>
        item.type.category == BizEntityTypeCategory.Tenant
      );
      // const firstItemId = firstItem?.id || filteredLibraryData[0]?.id || '';
      // this.global.setSelectedBizEntityId(firstItemId);
    } finally {
      const elapsedMs = Date.now() - loadStartedAt;
      if (elapsedMs < MIN_SHIMMER_DURATION_MS) {
        await new Promise((resolve) => setTimeout(resolve, MIN_SHIMMER_DURATION_MS - elapsedMs));
      }
      this.isLoading.set(false);

      // Clear the structural entity flag now that initializeExplorer has consumed it.
      this.dashboardStructuralEntityId = null;

      // Restore viewer state if it was open before Explorer re-initialisation.
      // initializeExplorer resets explorerAttributes (including isApplicationDerivedContent),
      // so we re-apply viewer mode and the correct nav selection here.
      if (viewerWasOpen) {
        this.updateExplorerForViewer(true);
        if (preOpenParentId) {
          this.facade.updateCurrentSelectedItem(preOpenParentId);
        }
      }
    }
  }

  /**
   * Initialize library with category-specific data (for direct route access or programmatic category filtering)
   */
  private async initializeWithCategoryData(categoryParam?: BizEntityTypeCategory): Promise<void> {
    this.isLoading.set(true);
    try {
      // Special handling for Templates route - show ALL templates across all categories
      if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES) {
        this.logger.info('📋 Loading Templates view - showing all templates across categories');

        const libraryData = await this.facade.getAllBizEntities();
        this.setLibraryEntitiesSnapshot(libraryData || []);

        // Filter all entities to find templates (those with istemplate=true)
        const allTemplates = (libraryData || []).filter((entity) => {
          if (entity.additionalInfo && Array.isArray(entity.additionalInfo)) {
            return entity.additionalInfo.some((info) =>
              info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() === BooleanString.TRUE
            );
          }
          return false;
        });

        this.logger.info(`✅ Found ${allTemplates.length} template(s) across all categories`);

        // Prune nav to only ancestors of templates so the hierarchy reflects
        // what is actually visible in the content area (same rule used on
        // category-specific document routes).
        const templatesAncestorsForNav = allTemplates.length > 0
          ? findAncestors(allTemplates, libraryData || [])
          : [];

        // Initialize explorer with all templates using the currently selected parent
        // const templatesSelectedParentId = this.explorerState.getSelectedParentId();
        // await this.explorerState.initializeExplorerWithCategoryData(
        //   templatesAncestorsForNav,
        //   allTemplates,
        //   templatesSelectedParentId,
        //   (item) => this.facade.getContextMenuItems(item),
        //   (info) => this.onContextMenuClicked(info),
        //   (itemId) => this.onItemSelected(itemId),
        //   (itemId) => this.onListItemClicked(itemId),
        //   (entity) => this.handleItemDoubleClick(entity)
        // );

        // Templates route initial load should mirror other category routes:
        // select a root context first, then show only that node's direct
        // template-visible children (instead of a flat list of all templates).
        const existingSelectedParentId = this.explorerState.getSelectedParentId();
        const appRootNodes = (libraryData || []).filter(
          (entity) => entity.type?.category === BizEntityTypeCategory.ApplicationRootNode
        );

        let templatesSelectedParentId = existingSelectedParentId;
        if (!templatesSelectedParentId) {
          templatesSelectedParentId = appRootNodes[0]?.id || '';
        }

        if (
          templatesSelectedParentId &&
          appRootNodes.length > 0 &&
          !appRootNodes.some((root) => root.id === templatesSelectedParentId)
        ) {
          templatesSelectedParentId = appRootNodes[0]?.id || '';
        }

        if (templatesSelectedParentId) {
          this.explorerState.setSelectedParentId(templatesSelectedParentId);
          if (!this.isInModal) {
            this.global.setSelectedBizEntityId(templatesSelectedParentId);
          }
        }

        let initialTemplateContent: IBizEntity[] = [];
        if (templatesSelectedParentId) {
          const rootChildren = await this.facade.getBizEntityByParent(templatesSelectedParentId);
          initialTemplateContent = await this.filterTemplateVisibleChildren(rootChildren || []);
        }

        await this.explorerState.initializeExplorerWithCategoryData(
          templatesAncestorsForNav,
          initialTemplateContent,
          templatesSelectedParentId,
          (item) => this.facade.getContextMenuItems(item),
          (info) => this.onContextMenuClicked(info),
          (itemId) => this.onItemSelected(itemId),
          (itemId) => this.onListItemClicked(itemId),
          (entity) => this.handleItemDoubleClick(entity),
          !this.isInModal
        );

        // this.global.setSelectedBizEntityId(templatesSelectedParentId);
        this.isLoading.set(false);
        return;
      }

      // Determine category based on route or provided parameter
      let category: BizEntityTypeCategory | null = null;

      // If a category parameter is provided, use it directly
      if (categoryParam !== undefined) {
        category = categoryParam;
      }
      // Otherwise, determine from route URL
      else if (this.routeUrl === Routes.DOCUMENTS_PDF) {
        category = BizEntityTypeCategory.HayagrivaFilePDF;
      } else if (this.routeUrl === Routes.DOCUMENTS_DOC) {
        category = BizEntityTypeCategory.HayagrivaFileDoc;
      } else if (this.routeUrl === Routes.DOCUMENTS_MARKDOWN) {
        category = BizEntityTypeCategory.HayagrivaFileMarkdown;
      } else if (this.routeUrl === Routes.DOCUMENTS_SPREADSHEET) {
        category = BizEntityTypeCategory.HayagrivaFileSpreadsheet;
      } else if (this.routeUrl === Routes.DOCUMENTS_IMAGES) {
        category = BizEntityTypeCategory.HayagrivaFileImage;
      } else if (this.routeUrl === Routes.DOCUMENTS_VIDEOS) {
        category = BizEntityTypeCategory.HayagrivaFileVideo;
      } else if (this.routeUrl === Routes.DOCUMENTS_DIAGRAMS) {
        category = BizEntityTypeCategory.HayagrivaFileDrawing;
      } else if (this.routeUrl === Routes.DOCUMENTS_PRESENTATIONS) {
        category = BizEntityTypeCategory.HayagrivaFilePresentation;
      }

      if (category) {
        this.catergory = category;
        const shouldShowTemplatesOnly = categoryParam !== undefined;

        // Load both full library data (for navigation) and category data (for content) in parallel
        const [libraryData, categoryData] = await Promise.all([
          this.facade.getAllBizEntities(),
          this.facade.getBizEntitiesByCategory(category)
        ]);
        this.setLibraryEntitiesSnapshot(libraryData || []);
        const existingSelectedParentId = this.explorerState.getSelectedParentId();
        const appRootNodes = (libraryData || []).filter(
          (entity) => entity.type?.category === BizEntityTypeCategory.ApplicationRootNode
        );

        // Match /libraries behavior: on first load, auto-select the first root node.
        // If a valid parent is already selected (e.g., later refresh), keep it.
        let categorySelectedParentId = existingSelectedParentId;
        if (!categorySelectedParentId) {
          categorySelectedParentId = appRootNodes[0]?.id || '';
        }

        // Ensure selected parent id is one of the available app roots.
        if (
          categorySelectedParentId &&
          appRootNodes.length > 0 &&
          !appRootNodes.some((root) => root.id === categorySelectedParentId)
        ) {
          categorySelectedParentId = appRootNodes[0]?.id || '';
        }

        // Persist selected parent so subsequent route logic reads the same context.
        if (categorySelectedParentId) {
          this.explorerState.setSelectedParentId(categorySelectedParentId);
          if (!this.isInModal) {
            this.global.setSelectedBizEntityId(categorySelectedParentId);
          }
        }

        // If categoryParam is provided (from "New from Template" button), filter to show only templates
        let filteredCategoryData = categoryData;
        if (shouldShowTemplatesOnly && categoryData) {
          this.logger.info(`📋 Filtering for templates in category: ${category}`);
          filteredCategoryData = categoryData.filter((entity) => {
            // Check if entity has additionalInfo with istemplate field set to true
            if (entity.additionalInfo && Array.isArray(entity.additionalInfo)) {
              return entity.additionalInfo.some((info) =>
                info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
                info.value?.toLowerCase() === BooleanString.TRUE
              );
            }
            return false;
          });
          this.logger.info(`✅ Found ${filteredCategoryData.length} template(s) out of ${categoryData.length} total files`);
        } else if (categoryData) {
          // For regular category routes, exclude templates
          filteredCategoryData = categoryData.filter((entity) =>
            !entity.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() === BooleanString.TRUE
            )
          );
          this.logger.info(`✅ Excluded templates: ${filteredCategoryData.length} items remaining out of ${categoryData.length} total`);
        }


        let ancestorsForNav: IBizEntity[] = [];
        if (categoryData != undefined && categoryData?.length > 0) {
          ancestorsForNav = findAncestors(filteredCategoryData || [], libraryData || []);
        } else {
          filteredCategoryData = [];
        }
        // Pass the ancestor-pruned nav data directly. When the category
        // has no items, this is an empty array — and combined with the
        // up-front `resetExplorerState()` call in `ngOnInit`, the nav
        // child's `hierarchyNodes` stay empty (the upstream
        // `prepareData()` early-return on empty data is benign because
        // there is no prior hierarchy to leak through).
        const categoryNavData = ancestorsForNav;

        // Initial category-route content should mirror /libraries behavior:
        // show only the currently selected parent's direct children (files in
        // this category + folders that contain this category recursively).
        let initialCategoryContent: IBizEntity[] = [];
        if (categorySelectedParentId && this.catergory !== null) {
          const rootChildren = await this.facade.getBizEntityByParent(categorySelectedParentId);
          const visibleChildren: IBizEntity[] = [];

          for (const child of rootChildren || []) {
            const isTemplate = child.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() === BooleanString.TRUE
            );

            if (shouldShowTemplatesOnly) {
              if (child.type?.category === this.catergory) {
                visibleChildren.push(child);
                continue;
              }
            } else if (child.type?.category === this.catergory && !isTemplate) {
              visibleChildren.push(child);
              continue;
            }

            if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
              // Previous behavior kept only folders that had category-matching descendants.
              // const hasMatchingContent = await this.folderContainsCategoryFiles(child.id, this.catergory);
              // if (hasMatchingContent) {
              //   visibleChildren.push(child);
              // }

              // New behavior: show all structural folders in category routes.
              visibleChildren.push(child);
            }
          }

          initialCategoryContent = shouldShowTemplatesOnly
            ? await this.filterTemplateVisibleChildren(visibleChildren)
            : visibleChildren;
        }

        // Initialize explorer with full library structure using the currently selected parent
        await this.explorerState.initializeExplorerWithCategoryData(
          libraryData || [],
          initialCategoryContent,
          categorySelectedParentId,
          (item) => this.facade.getContextMenuItems(item),
          (info) => this.onContextMenuClicked(info),
          (itemId) => this.onItemSelected(itemId),
          (itemId) => this.onListItemClicked(itemId),
          (entity) => this.handleItemDoubleClick(entity),
          !this.isInModal
        );

        // Use the currently selected parent id (fallback to default if none)
        // this.global.setSelectedBizEntityId(categorySelectedParentId);
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Refresh navigation tree after structural entity update (rename)
   * This updates the navigation tree while maintaining the current folder context
   */
  /**
   * Recompute and apply the navigation tree pruned to ancestors of items
   * matching the current category-specific route (e.g. /documents/pdf,
   * /documents/templates). This mirrors the rule used on initial load in
   * `initializeWithCategoryData` so the hierarchy stays in sync after
   * update / delete / refresh operations.
   *
   * No-op when the user is not on a category-specific route.
   *
   * @param prefetched Optionally reuse already-fetched data to avoid extra
   *                   API round-trips (e.g. from `loadCategorySpecificData`).
   */
  private async refreshCategoryNavTree(prefetched?: {
    libraryData?: IBizEntity[];
    categoryData?: IBizEntity[];
  }): Promise<void> {
    if (!this.isCategorySpecificRoute(this.routeUrl)) {
      return;
    }

    try {
      const libraryData =
        prefetched?.libraryData ?? (await this.facade.getAllBizEntities()) ?? [];
      this.setLibraryEntitiesSnapshot(libraryData);

      let matchingEntities: IBizEntity[] = [];

      if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES) {
        // Templates route: ancestors of every template across all categories
        matchingEntities = libraryData.filter((entity) =>
          entity.additionalInfo?.some((info) =>
            info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
            info.value?.toLowerCase() === BooleanString.TRUE
          ) ?? false
        );
      } else if (this.catergory !== null && this.catergory !== undefined) {
        // Category route: ancestors of files in this category, excluding templates
        const categoryData =
          prefetched?.categoryData ??
          (await this.facade.getBizEntitiesByCategory(this.catergory)) ??
          [];
        matchingEntities = categoryData.filter((entity) =>
          !entity.additionalInfo?.some((info) =>
            info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
            info.value?.toLowerCase() === BooleanString.TRUE
          )
        );
      } else {
        return;
      }

      const navData = matchingEntities.length > 0
        ? findAncestors(matchingEntities, libraryData)
        : [];

      this.explorerState.setNavData(navData);
      this.explorerState.refreshExplorer();
    } catch (err) {
      this.logger.error(`❌ Error refreshing category nav tree: ${err}`);
    }
  }

  private async refreshNavigationAfterStructuralUpdate(): Promise<void> {
    this.logger.info('🔄 refreshNavigationAfterStructuralUpdate called');

    this.isLoading.set(true);
    try {
      const currentParentId = this.explorerState.getSelectedParentId();
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);

      this.logger.info(`🔄 Current parent ID: ${currentParentId}`);
      this.logger.info(`🔄 Is category route: ${isCategoryRoute}`);

      if (isCategoryRoute) {
        // For category routes, reload the category-filtered data
        this.logger.info('🔄 Reloading category-specific data while maintaining context');
        await this.loadCategorySpecificData();
      } else {
        // For regular library routes, refresh the navigation tree and maintain current folder view
        this.logger.info('🔄 Refreshing navigation tree while maintaining current folder');

        // Get fresh data from API
        const libraryData = await this.facade.getAllBizEntities();
        if (!libraryData) {
          this.logger.error('❌ Failed to fetch library data');
          return;
        }
        this.setLibraryEntitiesSnapshot(libraryData);

        // Get the current folder's children
        let children: IBizEntity[] | undefined;

        if (currentParentId) {
          children = await this.facade.getBizEntityByParent(currentParentId);
        } else {
          // If no current parent, get the first item's children
          const firstItem = libraryData.find((item: any) =>
            item.type.category == BizEntityTypeCategory.Tenant
          );
          const firstItemId = firstItem?.id || libraryData[0]?.id || '';
          children = libraryData.filter(item => item.parentId === firstItemId);
        }

        // Update the explorer with fresh navigation data but maintain current content
        const currentAttrs = this.explorerAttributes();
        const contentListData = await this.dataTransformer.buildListView(
          filterBizEntitiesForApplicationExplorer(children || []),
          (info: any) => this.onContextMenuClicked(info),
          (itemId: string) => this.onItemSelected(itemId),
          (itemId: string) => this.onListItemClicked(itemId),
          (entity: IBizEntity) => this.handleItemDoubleClick(entity)
        );

        const updatedAttrs = {
          ...currentAttrs,
          data: filterBizEntitiesForApplicationExplorer(libraryData),
          explorerNavAttributes: {
            ...currentAttrs.explorerNavAttributes!,
            data: filterBizEntitiesForApplicationExplorer(libraryData),
            currentSelectedItem: currentParentId || currentAttrs.explorerNavAttributes?.currentSelectedItem,
          },
          explorerContentAttributes: {
            ...currentAttrs.explorerContentAttributes!,
            data: filterBizEntitiesForApplicationExplorer(children || []),
            listData: contentListData as any,
            currentSelectedItem: currentParentId || currentAttrs.explorerContentAttributes?.currentSelectedItem,
          }
        };

        this.explorerState.explorerAttributes.set(updatedAttrs);
        this.explorerState.refreshExplorer();

        this.logger.info('✅ Navigation tree refreshed while maintaining current folder context');
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Refresh current folder after entity creation/deletion
   * This maintains the user's current navigation context
   */
  private async refreshCurrentFolder(): Promise<void> {
    this.logger.info('🔄 refreshCurrentFolder called');
    this.logger.debug('🔄 refreshCurrentFolder called');

    // Check if viewer is currently open
    const isViewerOpen = this.explorerAttributes().explorerContentAttributes?.isApplicationDerivedContent || false;

    // If viewer is open and we're just creating/updating a file (not deleting a structural entity),
    // skip refresh to avoid closing the viewer
    // Note: For structural entity deletion (folders), we still need to refresh even if viewer is open
    if (isViewerOpen && !this.isAutoOpeningCreatedFile) {
      this.logger.info('📂 Viewer is open, skipping folder refresh to maintain viewer state');
      this.logger.info('ℹ️ Note: Navigation will be refreshed when viewer is closed');
      return;
    }

    this.isLoading.set(true);
    try {
      const currentParentId = this.explorerState.getSelectedParentId();
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);

      this.logger.info(`🔄 Current parent ID: ${currentParentId}`);
      this.logger.info(`🔄 Is category route: ${isCategoryRoute}`);

      const children = await this.facade.getBizEntityByParent(currentParentId);
      const bizentity = await this.facade.getAllBizEntities();
      this.setLibraryEntitiesSnapshot(bizentity || []);

      const filteredChildren = bizentity?.find((child) => child.id == currentParentId);
      this.logger.info(`🔄 Doing full refresh to get latest data ${filteredChildren?.name || ''}`);
      if (filteredChildren?.type?.functionType) { }
      if (isCategoryRoute) {
        // For category routes, reload the category-filtered data
        this.logger.info('🔄 Reloading category-specific data');
        await this.loadCategorySpecificData();
      } else if (currentParentId) {
        // For regular library routes, update the explorer in-place using the
        // already-fetched data. Avoid initializeLibraryData() here because it
        // sets preSelectedItemId=currentParentId which triggers the Explorer's
        // navigateToPreSelectedItem(), causing it to fire onItemSelected() with
        // the PARENT of currentParentId — navigating away from the current folder.
        const allData = bizentity || [];
        let currentChildren = children || [];

        // Apply the same template filter as initializeLibraryData
        if (this.routeUrl !== Routes.DOCUMENTS_TEMPLATES) {
          currentChildren = currentChildren.filter((entity) =>
            !entity.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() === BooleanString.TRUE
            )
          );
        }

        this.explorerState.refreshInPlace(
          allData,
          currentParentId,
          currentChildren,
          (info) => this.onContextMenuClicked(info),
          (itemId) => this.onItemSelected(itemId),
          (itemId) => this.onListItemClicked(itemId),
          (entity) => this.handleItemDoubleClick(entity)
        );
      } else {
        // Fallback: full reinitialization if we don't have context
        this.logger.info('🔄 No context, full reinitialization');
        await this.initializeLibraryData();
      }
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Setup query parameter handling for navigation
   */
  private setupQueryParamHandling(): void {
    let lastProcessedParams: string | null = null;

    this.subscriptions.push(
      this.facade.queryParams$.subscribe(async params => {
        const item = params[QueryParams.ITEM];
        const selectedItemId = params[QueryParams.SELECTED_ITEM];
        const openFile = params[QueryParams.OPEN_FILE];
        const bizEntityId = params[QueryParams.BIZ_ENTITY_ID];

        const currentParamsKey = JSON.stringify({ item, selectedItemId, openFile, bizEntityId });

        if (currentParamsKey === lastProcessedParams || (!item && !selectedItemId && !openFile && !bizEntityId)) {
          return;
        }

        lastProcessedParams = currentParamsKey;

        // Handle openFile with bizEntityId (new functionality)
        if (openFile === QueryParamValues.TRUE && bizEntityId) {
          this.logger.info(`📂 Auto-opening file with bizEntityId: ${bizEntityId}`);
          // setTimeout(() => {
            this.handleAutoOpenFile(bizEntityId);
          // }, 500)
          return;
        }

        if (item) {
          const normalizedItemId = this.normalizeItemParam(item);

          if (!normalizedItemId) {
            return;
          }

          const isDashboardSource = sessionStorage.getItem(SessionStorageKeys.DASHBOARD_NAVIGATION_SOURCE) === SessionStorageValues.DASHBOARD;

          // Dashboard deep-links: if tryOpenDashboardFileEarly already opened this file
          // (viewer is showing the same entity), skip to avoid a redundant re-open.
          // Otherwise open it now — this covers the fallback case where the early open
          // failed (e.g. getBizEntityById returned nothing) or the component was not
          // recreated (same-route re-navigation without ngOnInit running).
          if (isDashboardSource) {
            const currentEntity = this.appStateService.selectedBizEntityForEdit;
            if (currentEntity?.id?.toString() === normalizedItemId) {
              return; // Already open — nothing to do (operational entity)
            }
            // Structural entities are handled by tryOpenDashboardFileEarly via
            // initializeLibraryData preselection. Check if the explorer is already
            // showing the folder (selectedParentId matches) to avoid a redundant cycle.
            const currentParentId = this.explorerState.getSelectedParentId();
            if (currentParentId?.toString() === normalizedItemId) {
              return; // Structural entity already preselected — nothing to do
            }
            await this.openDashboardPinnedFile(normalizedItemId);
            return;
          }

          const attrs = await this.bizEntityStateStore.getState(this.bizEntityTypeStateStore);
          if (!attrs || attrs === undefined) {
            this.logger.warn('⚠️ No data available in bizEntityStateStore to find item for auto-open');
          }
          const selectedItem = attrs?.find(d => d.id?.toString() === normalizedItemId.toString());
          if (selectedItem) {
            this.handleItemDoubleClick(selectedItem);
          }
          return;
        }

        if (selectedItemId) {
          this.facade.updateCurrentSelectedItem(selectedItemId);
          this.explorerState.setSelectedParentId(selectedItemId);
          this.global.setSelectedBizEntityId(selectedItemId);

          this.facade.navigateWithQueryParams({});
        }
      })
    );
  }

  /**
   * Handle auto-opening file from query parameter
   */
  private async handleAutoOpenFile(bizEntityId: string): Promise<void> {
    try {
      // First, check if the file is already loaded in current data
      // const attrs = this.explorerAttributes();
      const attrs = await this.bizEntityStateStore.getState(this.bizEntityTypeStateStore);
      let fileToOpen = attrs?.find(d => d.id == bizEntityId);
      // If not found in current data, fetch all entities to find it
      if (!fileToOpen) {
        this.logger.info(`🔍 File not found in current data, fetching all entities...`);
        const allEntities = await this.facade.getAllBizEntities();
        this.setLibraryEntitiesSnapshot(allEntities || []);
        fileToOpen = allEntities?.find(entity => entity.id === bizEntityId);
      }

      if (fileToOpen) {
        this.logger.info(`✅ Found file: ${fileToOpen.name}, opening in editor...`);
        this.handleItemDoubleClick(fileToOpen);

        // Clear stored query parameters since we successfully handled them
        this.global.clearStoredQueryParameters();

        // Clear only the openFile and bizEntityId parameters, keep others intact
        // Use replaceUrl to avoid triggering route change events
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { [QueryParams.OPEN_FILE]: null, [QueryParams.BIZ_ENTITY_ID]: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      } else {
        this.logger.error(`❌ File with bizEntityId ${bizEntityId} not found`);
      }
    } catch (error) {
      this.logger.error(`❌ Error auto-opening file: ${error}`);
    }
  }

  /**
   * Attempts to open a dashboard-pinned file at the very start of ngOnInit —
   * before getAllBizEntities / initializeExplorer run — so the viewer appears
   * without waiting for the full Explorer initialisation.
   *
   * Uses getBizEntityById (single-entity fetch, much faster than getAllBizEntities)
   * to locate the file. Returns true when the viewer was successfully opened;
   * false when the preconditions are not met or the entity could not be found
   * (setupQueryParamHandling then acts as the fallback via openDashboardPinnedFile).
   *
   * initializeLibraryData restores the viewer state after it reinitialises
   * explorerAttributes (which resets isApplicationDerivedContent to false).
   */
  private async tryOpenDashboardFileEarly(): Promise<boolean> {
    const isDashboardSource =
      sessionStorage.getItem(SessionStorageKeys.DASHBOARD_NAVIGATION_SOURCE) === SessionStorageValues.DASHBOARD;
    const currentUrl = this.router.url;
    const params = new URLSearchParams(currentUrl.split('?')[1] || '');
    const fileId = this.normalizeItemParam(params.get('item') || params.get('selectedItem'));

    if (!isDashboardSource || !fileId) {
      return false;
    }

    this.logger.info(`⚡ Early open: fetching dashboard-pinned file ${fileId}`);
    try {
      // Single-entity fetch — much faster than getAllBizEntities.
      const entity = await this.facade.getBizEntityById(fileId);
      
      if (!entity) {
        this.logger.warn(`⚠️ Early open: entity ${fileId} not found — setupQueryParamHandling will retry`);
        return false;
      }

      this.logger.info(`✅ Early open: found "${entity.name}", opening viewer immediately`);

      // Structural entities (folders/libraries) are navigated into, not opened in the viewer.
      // Store the entity ID so initializeLibraryData can pass it as explorerPreSelectedId,
      // which causes the Explorer to preselect the folder in the nav and display its children
      // as the initial content — all in a single initialisation pass (no extra loading cycles).
      if (entity.type?.functionType === BizEntityFunctionTypes.Structural) {
        this.logger.info(`⚡ Early open: structural entity — storing as dashboard structural entity for preselection`);
        this.dashboardStructuralEntityId = entity.id;
        this.explorerState.setSelectedParentId(entity.id);
        if (!this.isInModal) {
          this.global.setSelectedBizEntityId(entity.id);
        }
        this.selectUploadFolder(entity.id);
        return true;
      }

      // Set the parent context so "Back" returns to the correct folder.
      const parentId = entity.parentId;
      if (parentId) {
        this.explorerState.setSelectedParentId(parentId);
        if (!this.isInModal) {
          this.global.setSelectedBizEntityId(parentId);
        }
        this.selectUploadFolder(parentId);
      }
      // Open the viewer — identical to a manual double-click.
      this.handleItemDoubleClick(entity);
      return true;
    } catch (error) {
      this.logger.warn(`⚠️ Early open failed: ${error}`);
      return false;
    }
  }

  /**
   * Directly opens a file that was pinned to the dashboard. Called explicitly from
   * setupQueryParamHandling when ?item= is present and DASHBOARD_NAVIGATION_SOURCE is
   * set. Does NOT rely on onItemSelected or any event-based chain — the parent context
   * and viewer are updated in a single, deterministic call.
   */
  private async openDashboardPinnedFile(fileId: string): Promise<void> {
    this.logger.info(`📌 Opening dashboard-pinned file: ${fileId}`);
    try {
      // Check state store first (fast path); fall back to a fresh fetch.
      const storeData = await this.bizEntityStateStore.getState(this.bizEntityTypeStateStore);
      let fileToOpen: IBizEntity | undefined = storeData?.find(
        (d: IBizEntity) => d.id?.toString() === fileId.toString()
      );

      if (!fileToOpen) {
        this.logger.info(`🔍 File not in state store, fetching all entities...`);
        const allEntities = await this.facade.getAllBizEntities();
        this.setLibraryEntitiesSnapshot(allEntities || []);
        fileToOpen = allEntities?.find(entity => entity.id === fileId);
      }

      if (!fileToOpen) {
        this.logger.error(`❌ Dashboard-pinned file not found: ${fileId}`);
        return;
      }

      this.logger.info(`✅ Found file: ${fileToOpen.name}, opening in viewer...`);

      // Set the file's parent folder as the explorer's selected context so that
      // clicking "Back" in the viewer returns to the correct folder — identical
      // to the context set by a manual double-click open.
      const parentId = fileToOpen.parentId;
      if (parentId) {
        this.explorerState.setSelectedParentId(parentId);
        if (!this.isInModal) {
          this.global.setSelectedBizEntityId(parentId);
        }
        this.selectUploadFolder(parentId);
        this.facade.updateCurrentSelectedItem(parentId);
      }

      // Open the file directly in the viewer.
      this.handleItemDoubleClick(fileToOpen);

    } catch (error) {
      this.logger.error(`❌ Error opening dashboard-pinned file: ${error}`);
    }
  }

  /**
   * Normalize `item` query params so both quoted JSON values and plain ids work.
   */
  private normalizeItemParam(itemParam: string | null | undefined): string {
    if (!itemParam) {
      return '';
    }

    try {
      const parsed = JSON.parse(itemParam);
      if (parsed !== null && parsed !== undefined) {
        return parsed.toString();
      }
    } catch {
      // Keep raw value for non-JSON ids (e.g. UUID strings).
    }

    return itemParam.toString();
  }

  /**
   * Setup handler for incoming selection from app state (benzaiten pattern)
   * This handles "View in Explorer" navigation from dashboard or other pages
   */
  private setupIncomingSelectionHandler(): void {
    this.subscriptions.push(
      this.appStateService.incomingBizEntitySelectionId$.subscribe((incomingId) => {
        if (!incomingId || !this._isActiveComponent) return;

        const attrs = this.explorerAttributes();
        let entity = attrs?.data?.find((e: any) => e.id?.toString() === incomingId.toString());

        if (!entity) {
          entity = attrs?.explorerContentAttributes?.data?.find(
            (e: any) => e.id?.toString() === incomingId.toString()
          );
        }

        if (entity) {
          this.updateListDataForHighlight(incomingId);
        } else {
          this.handleIncomingSelectionWithParentNavigation(incomingId);
        }
      })
    );
  }

  /**
   * Update explorer data to highlight an item (like benzaiten's updateListData)
   */
  private updateListDataForHighlight(itemId: string): void {
    this.appStateService.incomingBizEntitySelectionId = null;

    this.explorerAttributes.update(attrs => ({
      ...attrs,
      preSelectedItemId: itemId,
      explorerNavAttributes: {
        ...attrs.explorerNavAttributes!,
        currentSelectedItem: itemId
      },
      explorerContentAttributes: {
        ...attrs.explorerContentAttributes!,
        currentSelectedItem: itemId
      }
    }));

    this.global.setSelectedBizEntityId(itemId);
    this.onListItemClicked(itemId);
    this.dataRefreshedToken();
  }

  /**
   * Handle incoming entity selection that requires parent navigation
   */
  private async handleIncomingSelectionWithParentNavigation(incomingId: string): Promise<void> {
    try {
      // Find the entity to get its parent
      const attrs = await this.bizEntityStateStore.getState(this.bizEntityTypeStateStore);
      const entity = attrs?.find(e => e.id?.toString() === incomingId.toString());
      
      if (!entity) {
        this.logger.warn(`⚠️ Entity not found for incomingBizEntitySelectionId: ${incomingId}`);
        this.appStateService.incomingBizEntitySelectionId = null;
        return;
      }

      this.logger.info(`✅ Found entity: ${entity.name}, parent: ${entity.parentId}`);
      
      // Navigate to the parent folder to load the correct view
      if (entity.parentId) {
        await this.onItemSelected(entity.parentId);
      }
      
      // Wait for explorer to update before setting selection
      setTimeout(() => {
        this.updateListDataForHighlight(incomingId);
      }, 150);
      
    } catch (error) {
      this.logger.error(`❌ Error handling incoming selection: ${error}`);
      this.appStateService.incomingBizEntitySelectionId = null;
    }
  }

  /**
   * Setup route change monitoring to load category-specific data
   */
  private setupRouteMonitoring(): void {
    this.subscriptions.push(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe(async (event: any) => {
          const newUrl = event.urlAfterRedirects || event.url;

          // Extract the base path without query parameters
          const newBasePath = newUrl.split('?')[0];
          const currentBasePath = this.routeUrl.split('?')[0];

          // Only reload if the base route actually changed (not just query params)
          if (newBasePath !== currentBasePath) {
            this.routeUrl = newUrl;
            await this.loadCategorySpecificData();
          } else {
            // Just update the stored URL without reloading
            this.routeUrl = newUrl;
          }
        })
    );
  }

  /**
   * Check for addnew query parameter and trigger FAB button
   * Also handles parentId and typeId for targeted menu item triggering
   */
  private checkForAddNewQueryParam(): void {
    this.subscriptions.push(
      this.route.queryParams.subscribe(params => {
        if (params[QueryParams.ADD_NEW] === QueryParamValues.TRUE) {
          // Set selected parent ID if provided in query params
          if (params[QueryParams.PARENT_ID]) {
            this.explorerState.setSelectedParentId(params[QueryParams.PARENT_ID]);
          }

          this.logger.info('🔘 addnew=true detected in URL, triggering FAB button click');

          // Store parentId and typeId from query params
          this.queryParentId = params[QueryParams.PARENT_ID] || null;
          this.queryTypeId = params[QueryParams.TYPE_ID] || null;

          this.logger.info(`📍 Query parentId: ${this.queryParentId}, typeId: ${this.queryTypeId}`);

          // Set flag to allow the click to proceed
          this.isAutoOpeningFromQuery = true;

          // Wait for view to be initialized
          setTimeout(() => {
            this.openFABMenuProgrammatically();

            // If typeId is provided, trigger the specific nested menu item
            if (this.queryTypeId) {
              setTimeout(() => {
                this.triggerNestedMenuByTypeId(this.queryTypeId!);
              }, 500); // Wait for FAB menu to open
            }

            // Remove the query parameters to prevent triggering again
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: { [QueryParams.ADD_NEW]: null, [QueryParams.PARENT_ID]: null, [QueryParams.TYPE_ID]: null },
              queryParamsHandling: 'merge',
              replaceUrl: true
            }).then(() => {
              // Clear stored query parameters since we successfully handled them
              this.global.clearStoredQueryParameters();

              // Reset flag after navigation completes
              setTimeout(() => {
                this.isAutoOpeningFromQuery = false;
              }, 100);
            });
          }, 300);
        }
      })
    );
  }

  /**
   * Trigger a specific nested menu item by typeId
   */
  private triggerNestedMenuByTypeId(typeId: string): void {
    this.logger.info(`🎯 Attempting to trigger menu item with typeId: ${typeId}`);

    // Find the menu item with matching typeId (10533 for the nested context menu)
    const menuItems = this.menuItems();

    // For typeId 10533, we need to find the parent menu item and then trigger its nested item
    for (const menuItem of menuItems) {
      if (menuItem.items && Array.isArray(menuItem.items)) {
        // Check if any nested item matches or if this is the category we're looking for
        const availableTypes = this.availableBizEntityTypes();
        const matchingItem = availableTypes.find((item: any) => item.id?.toString() === typeId || item.category?.toString() === typeId);

        if (matchingItem) {
          this.logger.info(`✅ Found matching item: ${matchingItem.name}, category: ${matchingItem.category}`);

          // Trigger the first submenu item (usually "Create" or "Upload")
          if (menuItem.items && menuItem.items.length > 0) {
            const firstSubItem = menuItem.items[0];
            this.logger.info(`🔘 Triggering submenu item: ${firstSubItem.label}`);

            if (firstSubItem.command) {
              // Create a mock event with the matching item
              firstSubItem.command({ item: matchingItem });
            }
          }
          break;
        }
      }
    }
  }

  /**
   * Programmatically trigger the FAB button click
   */
  private openFABMenuProgrammatically(): void {
    if (this.isOpen) {
      return; // Menu is already open
    }

    // Find the FAB button element (using classes that actually exist on the button)
    const fabButton = document.querySelector('.position-fixed.fixed-btn.end-0.rounded-circle') as HTMLElement;

    if (!fabButton) {
      this.logger.warn('FAB button not found');
      return;
    }

    this.logger.info('🔘 Clicking FAB button programmatically');

    // Trigger the actual button click
    fabButton.click();
  }

  /**
   * Load category-specific data based on current route.
   *
   * Delegates to `initializeWithCategoryData` so route-change navigation
   * uses the SAME atomic explorer update path as the initial route load.
   * The earlier two-step approach (`reinitializeExplorerWithData` then
   * `refreshCategoryNavTree`) produced two separate signal updates and hit
   * a timing bug in the underlying Explorer's nav child where its
   * `prepareData()` early-returns on empty data — leaving stale hierarchy
   * from the previous route visible whenever the new route had no items.
   */
  private async loadCategorySpecificData(): Promise<void> {
    const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);

    if (isCategoryRoute) {
      // Reset the cached category so `initializeWithCategoryData` resolves
      // it freshly from the current route (otherwise the previous route's
      // category leaks into cache keys and downstream logic).
      this.catergory = null;
      await this.initializeWithCategoryData();
    } else if (this.routeUrl.includes('/libraries')) {
      // If we switched back to the general library route, reload all data
      await this.initializeLibraryData();
    }
  }

  /**
   * Reinitialize explorer with new data (used when switching between category routes)
   */
  private async reinitializeExplorerWithData(data: IBizEntity[]): Promise<void> {
    // For category routes, we need to maintain the full navigation structure
    // So we update the content area rather than reinitializing everything
    const selectedParentId = this.explorerState.getSelectedParentId();

    // Filter out excluded categories before passing to explorer
    const filteredData = filterBizEntitiesForApplicationExplorer(data);

    this.explorerState.updateExplorerWithChildren(
      selectedParentId,
      filteredData,
      (item) => this.facade.getContextMenuItems(item),
      (itemId) => this.onItemSelected(itemId),
      (itemId) => this.onListItemClicked(itemId),
      (entity) => this.handleItemDoubleClick(entity)
    );
  }

  /**
   * Check if the route is a category-specific route
   */
  private isCategorySpecificRoute(url: string): boolean {
    const normalizedUrl = url.split('?')[0];

    return normalizedUrl === Routes.DOCUMENTS_PDF ||
      normalizedUrl === Routes.DOCUMENTS_DOC ||
      normalizedUrl === Routes.DOCUMENTS_MARKDOWN ||
      normalizedUrl === Routes.DOCUMENTS_SPREADSHEET ||
      normalizedUrl === Routes.DOCUMENTS_IMAGES ||
      normalizedUrl === Routes.DOCUMENTS_VIDEOS ||
      normalizedUrl === Routes.DOCUMENTS_DIAGRAMS ||
      normalizedUrl === Routes.DOCUMENTS_PRESENTATIONS ||
      normalizedUrl === Routes.DOCUMENTS_TEMPLATES;
  }
  // ==================== Event Handlers ====================

  /**
   * Handle item selection in navigation
   */
  async onItemSelected(itemId: string): Promise<void> {
    if (this.facade.isViewerOpening() || this.shouldBlockNavigation(itemId)) {
      return;
    }
    const navAttrs = this.explorerAttributes().explorerNavAttributes;
    if (navAttrs) {
      navAttrs.isLoading = false;
    }
    // const item = this.explorerAttributes().data?.find((item) => item.id === itemId);
    // if (item) {
    //   console.log('item in explorerAttributes:', item);
    // }
    // Set loading state immediately and close any open viewer
    const currentAttrs = this.explorerAttributes();
    if (currentAttrs.explorerContentAttributes?.isApplicationDerivedContent) {
      this.logger.info('🔒 Closing viewer before navigating to new folder');
      await this.fileViewerContainer?.prepareForCollaboraClose();
      this.updateExplorerForViewer(false);
    }
    this.isLoading.set(true);

    try {
      this.explorerState.setSelectedParentId(itemId);
      if (!this.isInModal) {
        this.global.setSelectedBizEntityId(itemId);
      }
      this.selectUploadFolder(itemId);

      // Check if we're on a category-specific route (like /documents/pdf)
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);
      const shouldShowTemplatesOnly = this.isInModal || this.routeUrl === Routes.DOCUMENTS_TEMPLATES;
      let children: IBizEntity[] | undefined;
      if (isCategoryRoute && this.routeUrl === Routes.DOCUMENTS_TEMPLATES) {
        // On the templates route, fetch all children as-is — the template filter below will keep only templates + folders
        children = await this.facade.getBizEntityByParent(itemId);
      } else if ((isCategoryRoute && this.catergory !== null) || (this.isInModal && this.catergory !== null)) {
        // If on a category route, we need:
        // 1. Files filtered by category (e.g., only PDFs)
        // 2. Only folders that contain files of this category
        this.logger.info(`📂 Navigating to parent on category route: ${this.routeUrl}, filtering by category: ${this.catergory}`);

        // Get all children first
        const allChildren = await this.facade.getBizEntityByParent(itemId);
        // Filter to include:
        // - Category-specific files (e.g., PDFs)
        // - Folders that contain category-specific files (checked recursively)
        const filteredChildren: IBizEntity[] = [];

        if (allChildren) {
          for (const child of allChildren) {
            // Include if it's a file matching the category
            if (child.type?.category === this.catergory) {
              filteredChildren.push(child);
            }
            // Include all structural folders in category routes.
            else if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
              // Previous behavior kept only folders that had category-matching descendants.
              // const hasMatchingContent = await this.folderContainsCategoryFiles(child.id, this.catergory);
              // if (hasMatchingContent) {
              //   filteredChildren.push(child);
              // }

              filteredChildren.push(child);
            }
          }
        }

        children = shouldShowTemplatesOnly
          ? filteredChildren
          : filteredChildren.filter((child) => !this.isTemplateEntity(child));
      } else {
        // Otherwise, get all children
        const page = await this.facade.getBizEntityPage(itemId, 15, 0);
        // Reuse a previously loaded buffer instead of overwriting it, so returning to a
        // folder that was already scrolled past page 1 doesn't lose the extra pages.
        const existingBuffer = this.paginationBuffer.getItems(itemId);
        const bufferToUse = existingBuffer.length > page.items.length ? existingBuffer : page.items;
        this.paginationBuffer.setInitialPage(itemId, { items: bufferToUse, totalCount: page.totalCount });
        children = bufferToUse;
        children = shouldShowTemplatesOnly
          ? children
          : children?.filter((child) => !this.isTemplateEntity(child));
      }

      // Filter for templates when component is used in modal
      if (shouldShowTemplatesOnly && children) {
        this.logger.info('📋 Modal mode: Filtering to show only templates from  items');

        children = await this.filterTemplateVisibleChildren(children);

        this.logger.info(`✅ Filtered to ${children.length} items (templates + folders)`);
      }

      // Exclude system/excluded categories from children
      children = filterBizEntitiesForApplicationExplorer(children);

      // Re-check after all async operations: if the viewer opened (or is in the
      // process of opening) while we were awaiting data, abort the explorer
      // update. Without this guard, updateExplorerWithChildren would see
      // wasInViewerMode=true and — for Library/LibFolder parents — set
      // isApplicationDerivedContent=false, silently closing the viewer.
      // if (this.facade.isViewerOpen() || this.facade.isViewerOpening()) {
      //   return;
      // }

      this.explorerState.updateExplorerWithChildren(
        itemId,
        children as IBizEntity[],
        (info) => this.onContextMenuClicked(info),
        (id) => this.onItemSelected(id),
        (id) => this.onListItemClicked(id),
        (entity) => this.handleItemDoubleClick(entity)
      );

      this.facade.updateCurrentSelectedItem(itemId);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Check if a folder contains files of a specific category (recursively)
   */
  private async folderContainsCategoryFiles(
    folderId: string,
    category: BizEntityTypeCategory,
    visited: Set<string> = new Set()
  ): Promise<boolean> {
    const cacheKey = `${category}:${folderId}`;
    const cached = this.folderCategoryMatchCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    if (!folderId || visited.has(folderId)) {
      return false;
    }

    await this.ensureLibrarySnapshotLoaded();

    // Fast path: resolve from local snapshot when available.
    if (this.libraryChildrenByParent.size > 0) {
      const hasMatchingInSnapshot = this.folderContainsCategoryFilesInSnapshot(
        folderId,
        category,
        new Set<string>()
      );
      this.folderCategoryMatchCache.set(cacheKey, hasMatchingInSnapshot);

      // For complete snapshots (getAllBizEntities), trust in-memory result.
      // This avoids expensive per-folder API recursion.
      if (this.librarySnapshotComplete) {
        return hasMatchingInSnapshot;
      }

      if (hasMatchingInSnapshot) {
        return true;
      }
    }

    visited.add(folderId);

    try {
      const children = await this.facade.getBizEntityByParent(folderId);
      if (!children?.length) {
        this.folderCategoryMatchCache.set(cacheKey, false);
        return false;
      }

      for (const child of children) {
        if (child.type?.category === category) {
          this.folderCategoryMatchCache.set(cacheKey, true);
          return true;
        }
      }

      for (const child of children) {
        if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
          const hasMatchingContent = await this.folderContainsCategoryFiles(child.id, category, visited);
          if (hasMatchingContent) {
            this.folderCategoryMatchCache.set(cacheKey, true);
            return true;
          }
        }
      }

      this.folderCategoryMatchCache.set(cacheKey, false);
      return false;
    } catch (error) {
      this.logger.error(`Error checking folder contents: ${error}`);
      return false;
    }
  }

  /**
   * Returns true when the given entity is marked as a template.
   */
  private isTemplateEntity(entity: IBizEntity): boolean {
    return entity.additionalInfo?.some((info) =>
      info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
      info.value?.toLowerCase() === BooleanString.TRUE
    ) ?? false;
  }

  /**
   * Returns true when the folder contains at least one template in its
   * descendants (directly or recursively).
   */
  private async folderContainsTemplateFiles(folderId: string, visited: Set<string> = new Set()): Promise<boolean> {
    const cached = this.folderTemplateMatchCache.get(folderId);
    if (cached !== undefined) {
      return cached;
    }

    if (!folderId || visited.has(folderId)) {
      return false;
    }

    await this.ensureLibrarySnapshotLoaded();

    // Fast path: resolve from local snapshot when available.
    if (this.libraryChildrenByParent.size > 0) {
      const hasTemplatesInSnapshot = this.folderContainsTemplateFilesInSnapshot(
        folderId,
        new Set<string>()
      );
      this.folderTemplateMatchCache.set(folderId, hasTemplatesInSnapshot);

      // For complete snapshots (getAllBizEntities), trust in-memory result.
      // This avoids expensive per-folder API recursion on templates route.
      if (this.librarySnapshotComplete) {
        return hasTemplatesInSnapshot;
      }

      if (hasTemplatesInSnapshot) {
        return true;
      }
    }

    visited.add(folderId);

    try {
      const children = await this.facade.getBizEntityByParent(folderId);
      if (!children?.length) {
        this.folderTemplateMatchCache.set(folderId, false);
        return false;
      }

      for (const child of children) {
        if (
          child.type?.functionType === BizEntityFunctionTypes.Operational &&
          this.isTemplateEntity(child)
        ) {
          this.folderTemplateMatchCache.set(folderId, true);
          return true;
        }
      }

      for (const child of children) {
        if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
          const hasTemplates = await this.folderContainsTemplateFiles(child.id, visited);
          if (hasTemplates) {
            this.folderTemplateMatchCache.set(folderId, true);
            return true;
          }
        }
      }

      this.folderTemplateMatchCache.set(folderId, false);
      return false;
    } catch (error) {
      this.logger.error(`Error checking template folder contents: ${error}`);
      return false;
    }
  }

  /**
   * Keep only templates and structural entities that contain templates.
   */
  private async filterTemplateVisibleChildren(children: IBizEntity[]): Promise<IBizEntity[]> {
    const filteredChildren: IBizEntity[] = [];

    for (const child of children) {
      if (child.type?.functionType === BizEntityFunctionTypes.Operational) {
        if (this.isTemplateEntity(child)) {
          filteredChildren.push(child);
        }
        continue;
      }

      if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
        const hasTemplates = await this.folderContainsTemplateFiles(child.id);
        if (hasTemplates) {
          filteredChildren.push(child);
        }
      }
    }

    return filteredChildren;
  }

  private async refreshListAfterDelete(parentId: string, deletedEntityId?: string): Promise<void> {
    this.isLoading.set(true);
    try {
      // Check if we're on a category-specific route (like /documents/pdf)
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);

      if (isCategoryRoute && this.catergory) {
        // For category routes, we need to check if we're at the root level or in a specific folder
        if (!parentId) {
          // At root level of category route - reload all category data
          this.logger.info(`🗑️ Refreshing root category route: ${this.routeUrl}, reloading all category data`);

          await this.loadCategorySpecificData();
        } else {
          // In a specific folder on category route - filter that folder's children
          this.logger.info(`🗑️ Refreshing folder on category route: ${this.routeUrl}, parentId: ${parentId}, filtering by category: ${this.catergory}`);

          // Get all children first
          const allChildren = await this.facade.getBizEntityByParent(parentId);

          // Filter to include:
          // - Category-specific files (e.g., PDFs)
          // - Folders that contain category-specific files (checked recursively)
          // - Exclude the deleted entity
          const filteredChildren: IBizEntity[] = [];

          if (allChildren) {
            for (const child of allChildren) {
              // Skip the deleted entity
              if (deletedEntityId && child.id === deletedEntityId) {
                this.logger.info(`🗑️ Skipping deleted entity: ${child.name} (${child.id})`);
                continue;
              }

              // Include if it's a file matching the category (excluding templates —
              // template files are shown only on the /documents/templates route).
              const isTemplate = child.additionalInfo?.some((info) =>
                info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
                info.value?.toLowerCase() === BooleanString.TRUE
              );
              if (child.type?.category === this.catergory && !isTemplate) {
                filteredChildren.push(child);
              }

              // Include all structural folders in category routes.
              if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
                // Previous behavior kept only folders that had category-matching descendants.
                // const hasMatchingContent = await this.folderContainsCategoryFiles(child.id, this.catergory);
                // if (hasMatchingContent) {
                //   filteredChildren.push(child);
                // }

                filteredChildren.push(child);
              }
            }
          }

          // Update the explorer with the filtered children
          this.explorerState.updateExplorerWithChildren(
            parentId,
            filteredChildren as any,
            (item) => this.facade.getContextMenuItems(item),
            (itemId) => this.onItemSelected(itemId),
            (itemId) => this.onListItemClicked(itemId),
            (entity) => this.handleItemDoubleClick(entity)
          );
        }
      } else {
        // For regular routes, get all children and filter out deleted entity
        let children = await this.facade.getBizEntityByParent(parentId);

        // Filter out the deleted entity if provided
        if (deletedEntityId && children) {
          children = children.filter(child => child.id !== deletedEntityId);
          this.logger.info(`🗑️ Filtered out deleted entity from regular route`);
        }

        // Exclude template files on non-templates routes (templates belong
        // only to /documents/templates).
        if (children && this.routeUrl !== Routes.DOCUMENTS_TEMPLATES) {
          children = children.filter((child) =>
            !child.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() == AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() == BooleanString.TRUE
            )
          );
        }

        // Filter for templates if on the templates route
        if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES && children) {
          children = await this.filterTemplateVisibleChildren(children);
        }

        // Exclude system/excluded categories from children
        if (children) {
          children = filterBizEntitiesForApplicationExplorer(children);
        }

        this.explorerState.updateExplorerWithChildren(
          parentId,
          children as any,
          (item) => this.facade.getContextMenuItems(item),
          (itemId) => this.onItemSelected(itemId),
          (itemId) => this.onListItemClicked(itemId),
          (entity) => this.handleItemDoubleClick(entity)
        );

        // For category-style routes that fall through to the regular branch
        // (e.g. /documents/templates), previous behavior pruned nav hierarchy
        // to ancestors of matching items.
        // Keeping old call commented for rollback safety.
        // await this.refreshCategoryNavTree();
      }

      // Update the main data array with fresh entities
      try {
        const allEntities = await this.facade.getAllBizEntities();
        this.setLibraryEntitiesSnapshot(allEntities || []);
        if (allEntities) {
          const currentAttrs = this.explorerAttributes();
          const filteredEntities = filterBizEntitiesForApplicationExplorer(allEntities);

          // Filter out the deleted entity if provided
          let finalEntities = filteredEntities;
          if (deletedEntityId) {
            finalEntities = filteredEntities.filter(entity => entity.id !== deletedEntityId);
            this.logger.info(`🗑️ Removed deleted entity from main data array`);
          }

          // Filter for templates if on the templates route
          if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES && finalEntities) {
            finalEntities = await this.filterTemplateVisibleChildren(finalEntities);
            this.logger.info(`✅ Filtered final entities for templates route - ${finalEntities.length} entities with templates`);
          }

          const updatedAttrs = {
            ...currentAttrs,
            data: finalEntities,
            // Keep left-side navigation aligned with the same full dataset
            // so route refreshes don't collapse the hierarchy unexpectedly.
            explorerNavAttributes: {
              ...currentAttrs.explorerNavAttributes!,
              data: finalEntities,
              currentSelectedItem:
                this.explorerState.getSelectedParentId() ||
                currentAttrs.explorerNavAttributes?.currentSelectedItem,
            }
          };

          this.explorerState.explorerAttributes.set(updatedAttrs);
          this.logger.info(`✅ Updated explorerAttributes.data with ${finalEntities.length} entities after delete`);
        }
      } catch (error) {
        this.logger.error(`❌ Error updating main data array after delete: ${error}`);
      }

      this.logger.info('🗑️ List refreshed successfully after deletion');
    } catch (error) {
      this.logger.error(`❌ Error refreshing list after delete: ${error}`);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Refresh the list after update - handles both category and regular routes
   * Similar to refreshListAfterDelete but re-fetches data to show updated values
   */
  private async refreshListAfterUpdate(parentId: string): Promise<void> {
    this.isLoading.set(true);
    try {
      // Check if we're on a category-specific route (like /documents/pdf)
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);

      if (isCategoryRoute && this.catergory) {
        // For category routes, we need to check if we're at the root level or in a specific folder
        if (!parentId) {
          // At root level of category route - reload all category data
          this.logger.info(`🔄 Refreshing root category route: ${this.routeUrl}, reloading all category data`);

          await this.loadCategorySpecificData();
        } else {
          // In a specific folder on category route - filter that folder's children
          this.logger.info(`🔄 Refreshing folder on category route: ${this.routeUrl}, parentId: ${parentId}, filtering by category: ${this.catergory}`);

          // Get all children first (fresh data from API)
          const allChildren = await this.facade.getBizEntityByParent(parentId);

          // Filter to include:
          // - Category-specific files (e.g., PDFs)
          // - Folders that contain category-specific files (checked recursively)
          const filteredChildren: IBizEntity[] = [];

          if (allChildren) {
            for (const child of allChildren) {
              // if (child.type?.functionType === BizEntityFunctionTypes.Operational && this.routeUrl === Routes.DOCUMENTS_TEMPLATES) {
              //   if (child.additionalInfo && Array.isArray(child.additionalInfo)) {
              //     const isTemplate = child.additionalInfo.some((info) =>
              //       info.fieldMeta?.name === 'istemplate' &&
              //       info.value === BooleanString.TRUE
              //     );
              //     if (isTemplate) {
              //       filteredChildren.push(child);
              //       return;
              //     }
              //   }
              // }
              // Include if it's a file matching the category (excluding templates —
              // template files are shown only on the /documents/templates route).
              const isTemplate = child.additionalInfo?.some((info) =>
                info.fieldMeta?.name?.toLowerCase() == AdditionalFieldNames.IS_TEMPLATE &&
                info.value?.toLowerCase() == BooleanString.TRUE
              );
              if (child.type?.category === this.catergory && !isTemplate) {
                filteredChildren.push(child);
              }

              // Include all structural folders in category routes.
              if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
                // Previous behavior kept only folders that had category-matching descendants.
                // const hasMatchingContent = await this.folderContainsCategoryFiles(child.id, this.catergory);
                // if (hasMatchingContent) {
                //   filteredChildren.push(child);
                // }

                filteredChildren.push(child);
              }
            }
          }

          // Update the explorer with the filtered children
          this.explorerState.updateExplorerWithChildren(
            parentId,
            filteredChildren as any,
            (item) => this.facade.getContextMenuItems(item),
            (itemId) => this.onItemSelected(itemId),
            (itemId) => this.onListItemClicked(itemId),
            (entity) => this.handleItemDoubleClick(entity)
          );

          // After an update, previous behavior pruned the hierarchy to
          // category ancestors. Keep commented for rollback safety.
          // await this.refreshCategoryNavTree();
        }
      } else {
        // For regular routes, get all children (fresh data from API)
        let children = await this.facade.getBizEntityByParent(parentId);

        // Exclude template files on non-templates routes (templates belong
        // only to /documents/templates).
        if (children && this.routeUrl !== Routes.DOCUMENTS_TEMPLATES) {
          children = children.filter((child) =>
            !child.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() == AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() == BooleanString.TRUE
            )
          );
        }

        // Filter for templates if on the templates route
        if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES && children) {
          children = await this.filterTemplateVisibleChildren(children);
        }

        // Exclude system/excluded categories from children
        if (children) {
          children = filterBizEntitiesForApplicationExplorer(children);
        }

        this.explorerState.updateExplorerWithChildren(
          parentId,
          children as any,
          (item) => this.facade.getContextMenuItems(item),
          (itemId) => this.onItemSelected(itemId),
          (itemId) => this.onListItemClicked(itemId),
          (entity) => this.handleItemDoubleClick(entity)
        );

        // For category-style routes that fall through to the regular branch,
        // previous behavior pruned nav hierarchy to matching ancestors.
        // Keep commented for rollback safety.
        // await this.refreshCategoryNavTree();
      }

      // Update the main data array with fresh entities
      try {
        const allEntities = await this.facade.getAllBizEntities();
        this.setLibraryEntitiesSnapshot(allEntities || []);
        if (allEntities) {
          const currentAttrs = this.explorerAttributes();
          const filteredEntities = filterBizEntitiesForApplicationExplorer(allEntities);

          // Filter for templates if on the templates route
          let finalEntities = filteredEntities;
          if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES && finalEntities) {
            finalEntities = await this.filterTemplateVisibleChildren(finalEntities);
            this.logger.info(`✅ Filtered final entities for templates route - ${finalEntities.length} entities with templates`);
          }

          const updatedAttrs = {
            ...currentAttrs,
            data: finalEntities,
            // Keep left-side navigation aligned with the same full dataset
            // so route refreshes don't collapse the hierarchy unexpectedly.
            explorerNavAttributes: {
              ...currentAttrs.explorerNavAttributes!,
              data: finalEntities,
              currentSelectedItem:
                this.explorerState.getSelectedParentId() ||
                currentAttrs.explorerNavAttributes?.currentSelectedItem,
            }
          };

          this.explorerState.explorerAttributes.set(updatedAttrs);
          this.logger.info(`✅ Updated explorerAttributes.data with ${finalEntities.length} entities after list refresh`);
        }
      } catch (error) {
        this.logger.error(`❌ Error updating main data array after list refresh: ${error}`);
      }

      this.logger.info('🔄 List refreshed successfully after update');
    } catch (error) {
      this.logger.error(`❌ Error refreshing list after update: ${error}`);
    } finally {
      this.isLoading.set(false);
    }
  }

  /**
   * Check if navigation should be blocked
   */
  private shouldBlockNavigation(itemId: string): boolean {
    const attrs = this.explorerAttributes();
    if (!attrs.explorerContentAttributes?.isApplicationDerivedContent) {
      return false;
    }

    const currentData = attrs.data || [];
    const selectedItem = currentData.find(item => item.id === itemId);

    const isNavigable = selectedItem?.type?.isAlwaysNavigable === true ||
      selectedItem?.type?.category === BizEntityTypeCategory.HayagrivaLibrary ||
      selectedItem?.type?.category === BizEntityTypeCategory.HayagrivaLibFolder ||
      selectedItem?.type?.category === BizEntityTypeCategory.Tenant;

    return !isNavigable;
  }

  /**
   * Handle item double-click to open in viewer
   */
  private handleItemDoubleClick(item: IBizEntity): void {
    if (item?.rights?.read === false) {
      this.toastService.warning('You do not have permission to view this item.');
      return;
    }
    if (item.type.functionType == BizEntityFunctionTypes.Structural) {
      this.logger.info(`Structural folder double-clicked, navigating to folder: ${item.id}`);
      this.onItemSelected(item.id);
      return;
    }
    const opened = this.facade.openViewer(item);
    if (opened) {
      this.bizEntityId = item.id;
      this.mode = item.rights as IRights;
      const category = this.facade.isViewerOpen() ? item.type?.category : null;

      // Set the selected entity for use in menu actions (e.g., Save as template)
      this.appStateService.selectedBizEntityForEdit = item;
      this.logger.info(`✅ Set selectedBizEntityForEdit: ${item.name}, ID: ${item.id}`);

      if (category !== null && category !== undefined) {
        this.category = category;
        this.updateExplorerForViewer(true);
      }
    }
  }

  /**
   * Update explorer to show/hide viewer
   */
  private updateExplorerForViewer(isViewerActive: boolean): void {
    const currentAttrs = this.explorerAttributes();
    const updatedAttrs = {
      ...currentAttrs,
      explorerContentAttributes: {
        ...currentAttrs.explorerContentAttributes!,
        isApplicationDerivedContent: isViewerActive
      },
    };

    this.explorerState.explorerAttributes.set(updatedAttrs);
    this.explorerState.refreshExplorer();
  }

  prepareForCollaboraClose(): Promise<void> {
    return this.fileViewerContainer?.prepareForCollaboraClose() ?? Promise.resolve();
  }

  private async handleBackFromPreview(): Promise<void> {
    this.logger.info('⬅️ Back button clicked, closing preview');
    await this.fileViewerContainer?.prepareForCollaboraClose();
    const currentEntity = this.appStateService.selectedBizEntityForEdit;
    const dashboardNavigationSource = sessionStorage.getItem(SessionStorageKeys.DASHBOARD_NAVIGATION_SOURCE);
    const dashboardSelectedItem = sessionStorage.getItem(SessionStorageKeys.DASHBOARD_SELECTED_ITEM);
    const isDashboardSource = dashboardNavigationSource === SessionStorageValues.DASHBOARD;

    // Dashboard-opened entities can be lightweight projections where parentId is missing.
    // Resolve it before deciding the back-navigation refresh context.
    let resolvedParentId = currentEntity?.parentId ?? null;
    if (!resolvedParentId && isDashboardSource && currentEntity?.id) {
      try {
        const fullEntity = await this.facade.getBizEntityById(currentEntity.id);
        resolvedParentId = fullEntity?.parentId || null;
      } catch (error) {
        this.logger.warn(`⚠️ Failed to resolve parentId for dashboard-opened entity: ${error}`);
      }
    }

    const isDashboardBackNavigation =
      isDashboardSource &&
      // If dashboard item id is available, enforce match; if absent, still
      // treat as dashboard-origin navigation to avoid dropping this path.
      (dashboardSelectedItem == null || dashboardSelectedItem.toString() === currentEntity?.id?.toString()) &&
      resolvedParentId != null &&
      currentEntity?.id != null;

    // Clear the entity layout cache to force fresh thumbnail fetch when returning to dashboard
    // This ensures updated files show their latest thumbnails
    if (currentEntity?.id) {
      this.entityLayoutConfigService.clearEntityCache(currentEntity.id);
    }

    this.isOpen = false;
    // Close the viewer and return to list view
    this.updateExplorerForViewer(false);

    // Close the FAB menu if it's open
    if (this.isOpen) {
      this.menu.hideTieredMenu();
      // this.isOpen = false;
    }

    // Reset viewer state
    this.bizEntityId = '';
    this.category = 0;

    // Clear the selected entity since we're closing the viewer
    this.appStateService.selectedBizEntityForEdit = null;
    this.logger.info('✅ Cleared selectedBizEntityForEdit');

    // Refresh the list so any newly created files (whose refresh was deferred
    // while the viewer was open) are now visible. Also update the main data array.
    try {
      const currentExplorerParentId = this.explorerState.getSelectedParentId();

      // Dashboard -> template -> preview -> back:
      // restore template parent folder as current/preselected context.
      if (isDashboardBackNavigation && resolvedParentId) {
        this.explorerState.setSelectedParentId(resolvedParentId);
        this.global.setSelectedBizEntityId(resolvedParentId);
        await this.refreshListAfterUpdate(resolvedParentId);
        sessionStorage.removeItem(SessionStorageKeys.DASHBOARD_SELECTED_ITEM);
        sessionStorage.removeItem(SessionStorageKeys.DASHBOARD_NAVIGATION_SOURCE);
      }

      // Prefer the current explorer selection so "Back" restores the same folder
      // context the user was browsing before opening the viewer.
      else if (currentExplorerParentId) {
        await this.refreshListAfterUpdate(currentExplorerParentId);
        // Previous behavior used the opened file's parent id directly.
        // Keeping this as fallback for edge cases where explorer selection is empty.
        // } else if (currentEntity?.parentId != undefined && currentEntity?.parentId != null) {
        //   await this.refreshListAfterUpdate(currentEntity.parentId);
      } else if (currentEntity?.parentId != undefined && currentEntity?.parentId != null) {
        await this.refreshListAfterUpdate(currentEntity.parentId);
      } else {
        await this.refreshCurrentFolder();
      }

      // Fetch fresh data to ensure the newly created file is in the main data array
      const allEntities = await this.facade.getAllBizEntities();
      this.setLibraryEntitiesSnapshot(allEntities || []);
      if (allEntities) {
        const currentAttrs = this.explorerAttributes();
        const filteredEntities = filterBizEntitiesForApplicationExplorer(allEntities);
        const selectedParentId = this.explorerState.getSelectedParentId();

        // Filter for templates if on the templates route
        let finalEntities = filteredEntities;
        if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES && finalEntities) {
          finalEntities = await this.filterTemplateVisibleChildren(finalEntities);
          this.logger.info(`✅ Filtered final entities for templates route - ${finalEntities.length} entities with templates`);
        }

        // Update the main data array with fresh entities
        const updatedAttrs = {
          ...currentAttrs,
          data: finalEntities,
          // Keep left navigation in sync with the same full library set so
          // category routes remain consistent after returning from preview.
          explorerNavAttributes: {
            ...currentAttrs.explorerNavAttributes!,
            data: finalEntities,
            currentSelectedItem:
              selectedParentId ||
              currentAttrs.explorerNavAttributes?.currentSelectedItem,
          }
        };

        this.explorerState.explorerAttributes.set(updatedAttrs);
        this.explorerState.refreshExplorer();
        this.logger.info(`✅ Updated explorerAttributes.data with ${finalEntities.length} entities after closing preview`);
      }
    } catch (error) {
      this.logger.error(`❌ Error refreshing after preview close: ${error}`);
    }

    this.logger.info('✅ Returned to parent document view');
  }

  /**
   * Handle save as template action
   */
  private handleSaveAsTemplate(entity?: IBizEntity): void {
    this.logger.info(`📄 Save as template handler called with entity: ${entity?.name || entity?.id || 'undefined'}`);

    if (!entity) {
      this.logger.error('❌ No entity provided for save as template');
      return;
    }

    // Open the Add Folder off-canvas
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      OffCanvasComponents.SaveAsTemplate,
      OffCanvasIDsInDefaultLayout.l1_id,
      1
    );

    this.logger.info(`✅ Opened Save As Template off-canvas for template: ${entity.name}`);
  }

  /**
   * Handle pin to dashboard action
   */
  private handlePinToDashboard(entity?: IBizEntity): void {
    this.logger.info(`📌 Pin to Dashboard handler called with entity: ${entity?.name || entity?.id || 'undefined'}`);

    if (!entity) {
      this.logger.error('❌ No entity provided for pinning');
      return;
    }

    // Set the entity in app state so context menu service can access it
    this.appStateService.selectedBizEntityForEdit = entity;

    // This will trigger the context menu's pin functionality
    // The context menu service will handle showing dashboard options
    this.logger.info(`✅ Entity set for pinning: ${entity.name}, ID: ${entity.id}`);
  }

  /**
   * Handle list item clicked
   */
  onListItemClicked(itemId: string): void {
    const entity = this.explorerAttributes().data?.find(e => e.id.toString() === itemId.toString());

    if (entity) {
      this.logger.info(`📋 List item clicked: ${entity.name} (ID: ${entity.id})`);
      // Set the selected entity in app state for potential use by other components
      this.appStateService.selectedBizEntityForEdit = entity;
    } else {
      this.logger.warn(`⚠️ List item clicked but entity not found for ID: ${itemId}`);
    }
  }

  /**
   * Handle grid item clicked
   */
  onGridItemClicked(itemId: string): void {
    this.logger.debug(`Grid item clicked: ${itemId}`);
    // Grid update logic can be added here if needed
  }
  onForwardButtonClicked(itemId: string, actionOnOperationalElementClicked: boolean = true): void {

    const item = this.explorerAttributes().data?.find(entity => entity.id === itemId);

    // Handle operational leaf nodes — open in viewer instead of navigating
    if (
      actionOnOperationalElementClicked &&
      item?.type?.functionType === BizEntityFunctionTypes.Operational
    ) {
      if (item) {
        this.handleItemDoubleClick(item);
      }
      return;
    }

    // Navigate to item's children
    this.onItemSelected(itemId);
  }

  onHierarchyChange(event: IHierarchyItem): void {
    const bizEntityId = event.id.toString();
    const updatedParentBizEntityId = event.parentNodeId.toString();

    const item = this.explorerAttributes().data?.find(entity => entity.id.toString() === bizEntityId);
    if (item) {
      const oldParentId = item.parentId;

      this.logger.info(`🔄 Drag & Drop: Moving ${bizEntityId} from ${oldParentId} to ${updatedParentBizEntityId}`);

      // Optimistic update: Update the local data immediately
      item.parentId = updatedParentBizEntityId;
      item.additionalInfo = [];

      // Update the backend asynchronously without blocking the UI
      const updateResult = this.bizEntityStateStore.update(item);
      if (updateResult) {
        updateResult.then(() => {
          this.logger.info(`✅ Backend updated after drag and drop`);
          this.refreshCurrentFolder();
        }).catch((error: any) => {
          this.logger.error(`❌ Failed to update backend after drag and drop: ${error}`);
          // Revert the change if backend update fails
          item.parentId = oldParentId;
          this.refreshCurrentFolder();
        });
      }
    }
  }

  /**
   * Handle context menu click
   */
  async onContextMenuClicked(event: ContextMenuClickInfo): Promise<void> {
    let entity = this.explorerAttributes().data?.find(e => e.id.toString() === event.id.toString());
    if (entity) {
      this.openAndHydrateContextMenuForEntity(entity);
    } else {
      console.error('❌ Entity not found for context menu click, id:', event.id);
    }
    this.logger.info(`Updated selectedBizEntityForEdit: ${entity?.name || 'unknown'}`);
  }

  /**
   * Handle navigation item right-click
   */
  async onNavItemRightClick(event: any): Promise<void> {
    this.logger.info(`Right-click event on navigation item: ${JSON.stringify(event)}`);
    // Find the full entity data from the explorer's data
    const entity = this.explorerAttributes().data?.find(e => e.id.toString() === event.id!.toString());

    if (entity) {

      // Update global state with entity ID
      this.global.setUpdateBizEntity(entity.id);

      this.openAndHydrateContextMenuForEntity(entity);

      // Get context menu items - pass the entity with rights
      // const menuItems = this.contextMenuService.getMenu(entity);
      // console.log('Generated menu items:', menuItems);

      // Update SVG context menu items
      // this.svgContextMenuItems.set(menuItems);

      // Show the context menu at the mouse position
      setTimeout(() => {
        if (this.svgContextMenu && event.event) {
          this.svgContextMenu.showContextMenu(event.event);
        } else {
          console.error('❌ Cannot show context menu - missing svgContextMenu or event');
        }
      }, 0);
    } else {
      console.error('❌ Entity not found for id:', event.id);
    }
  }

  /**
   * Handle add button click
   */
  onAddButtonClicked(event?: MouseEvent | any): void {
    // If we have a proper MouseEvent, use it for positioning
    if (event && event.target && event.clientX !== undefined) {
      this.menu.showTieredMenu(event as MouseEvent);
    } else {
      // For cases where no proper event is provided, show menu without specific positioning
      // The ContextMenuComponent should handle default positioning
      this.menu.showTieredMenu(new MouseEvent('click'));
    }
    this.isOpen = true;
  }

  /**
   * Handle add button option click
   */
  onAddButtonOptionClicked(option: IAddButtonOptions): void {
    switch (option.id) {
      case UIActions.UPLOAD_FILE:
        this.handleFileUpload();
        break;
      case UIActions.CREATE_FOLDER:
        this.handleCreateFolder();
        break;
      case UIActions.IMPORT_URL:
        this.handleImportFromUrl();
        break;
    }
  }

  /**
   * Handle filter button click
   */
  onFilterButtonClicked(): void {
    this.logger.debug('Filter button clicked');
  }

  /**
   * Toggle context menu
   */
  async toggleBox(event: MouseEvent): Promise<void> {
    event.stopPropagation();

    const isViewerActive = this.explorerAttributes().explorerContentAttributes?.isApplicationDerivedContent || false;

    // Check if detail view icon was clicked (when in preview mode)
    if (isViewerActive) {
      const currentEntity = this.appStateService.selectedBizEntityForEdit || undefined;
      // Use async version to get proper Discussion disabled state
      setTimeout(async () => {
        const items = await this.menuService.buildFileMenuItemsAsync(
          () => this.handleBackFromPreview(),
          (entity) => this.handlePinToDashboard(entity),
          currentEntity,
          (entity) => this.handleSaveAsTemplate(entity),
          (entity) => this.handleSaveFromPreview(entity)
        );
        this.menuItems.set(items);
      }, 1000);
      this.logger.info('🔍 Detail view icon clicked in preview mode');
      this.isDetailViewIconClicked.set(true);
    } else {
      this.logger.info('➕ Plus icon clicked in browsing mode');
      this.isDetailViewIconClicked.set(false);
    }

    if (this.isOpen) {
      // Close the menu if it's already open
      this.logger.info('🔘 FAB button clicked, hiding menu');
      this.menu.hideTieredMenu();
      this.isOpen = false;
    } else {

      // Open the menu if it's closed
      this.logger.info(`🔘 FAB button clicked, showing menu with ${this.menuItems().length} items`);
      this.menu.showTieredMenu(event);
      this.isOpen = true;
    }
  }

  /**
   * Handle document click to close FAB menu
   */
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen) {
      // this.menu.hideTieredMenu();
      this.isOpen = false;
    }
  }

  // ==================== File Operations ====================

  /**
   * Handle file upload
   */
  private async handleFileUpload(item?: IBizEntity | any, event?: any): Promise<void> {
    // Use queryParentId if available from URL parameter, otherwise get current selected item
    let currentFolderId = this.queryParentId || this.facade.getCurrentSelectedItem() || BizEntityConstants.ORGANIZATIONS_LIB_ID;

    // If we have queryParentId, we need to fetch the folder entity
    let currentFolder: IBizEntity | undefined;

    if (this.queryParentId) {
      // Fetch the folder by its ID
      try {
        const allFolders = this.explorerAttributes().data || [];
        currentFolder = allFolders.find(folder => folder.id === this.queryParentId);

        // If not found in current data, try to fetch it
        if (!currentFolder) {
          this.logger.info(`📂 Fetching folder with ID: ${this.queryParentId}`);
          // For now, create a minimal folder object with the ID
          // The file upload component mainly needs the ID and functionType
          currentFolder = {
            id: this.queryParentId,
            type: {
              functionType: BizEntityFunctionTypes.Structural // Assume structural for upload target
            }
          } as any;
        }
      } catch (error) {
        this.logger.error(`❌ Error fetching folder: ${error}`);
      }
    } else {
      // Use the existing logic to find the folder
      currentFolder = this.explorerAttributes().data?.find(folder => folder.id === currentFolderId);
    }

    if (!currentFolder) {
      this.toastService.toast('Please select a folder first before uploading files.');
      return;
    }

    if (currentFolder.type?.functionType === BizEntityFunctionTypes.Operational) {
      this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      return;
    }

    this.selectedUploadFolder.set(currentFolder.id);

    if (this.fileUploadComponent) {
      this.fileUploadComponent.openFilePickerForDynamicUpload(currentFolder, item);
    } else {
      this.showUploadDialog.set(true);
    }

    // Clear queryParentId after use
    this.queryParentId = null;
  }

  /**
   * Select upload folder
   */
  selectUploadFolder(folderId: string): void {
    this.selectedUploadFolder.set(folderId);
  }

  /**
   * Handle upload completion
   */
  async onUploadCompleted(event: { folderId: string; uploadedFiles: any }): Promise<void> {
    this.isLoading.set(true);
    try {
      // Keep the in-memory hierarchy snapshot fresh after upload so category
      // route folder-visibility checks (used by onItemSelected) reflect the
      // newly uploaded file immediately, without route/page refresh.
      try {
        const latestEntities = await this.facade.getAllBizEntities();
        this.setLibraryEntitiesSnapshot(latestEntities || [], true);
      } catch (snapshotError) {
        this.logger.warn(`⚠️ Failed to refresh hierarchy snapshot after upload: ${snapshotError}`);
      }

      // Use the same filtering logic as onItemSelected and refreshListAfterDelete
      const isCategoryRoute = this.isCategorySpecificRoute(this.routeUrl);
      let folderChildren: IBizEntity[] | undefined;

      if (isCategoryRoute && this.catergory) {
        // If on a category route, apply filtering
        this.logger.info(`📤 Upload completed on category route: ${this.routeUrl}, filtering by category: ${this.catergory}`);

        const allChildren = await this.facade.getBizEntityByParent(event.folderId);
        const filteredChildren: IBizEntity[] = [];

        if (allChildren) {
          for (const child of allChildren) {
            // Exclude template files — they belong to /documents/templates only.
            const isTemplate = child.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() == AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() == BooleanString.TRUE
            );

            // On category-specific routes, only keep files that match the
            // active category. This preserves route semantics after uploads
            // (e.g., /documents/pdf must only show PDFs).
            if (child.type?.category === this.catergory && !isTemplate) {
              filteredChildren.push(child);
            }

            if (child.type?.functionType === BizEntityFunctionTypes.Structural) {
              // Previous behavior kept only folders that had category-matching descendants.
              // const hasMatchingContent = await this.folderContainsCategoryFiles(child.id, this.catergory);
              // if (hasMatchingContent) {
              //   filteredChildren.push(child);
              // }

              // New behavior: show all structural folders in category routes.
              filteredChildren.push(child);
            }
          }
        }

        folderChildren = filteredChildren;
      } else {
        // For regular routes, get all children
        folderChildren = await this.facade.getBizEntityByParent(event.folderId);
        // Exclude template files on non-templates routes (templates belong
        // only to /documents/templates). Use .filter() — pushing into the
        // same array we iterate would loop forever and leave templates in.
        if (folderChildren && this.routeUrl != Routes.DOCUMENTS_TEMPLATES) {
          folderChildren = folderChildren.filter((child) =>
            !child.additionalInfo?.some((info) =>
              info.fieldMeta?.name?.toLowerCase() == AdditionalFieldNames.IS_TEMPLATE &&
              info.value?.toLowerCase() == BooleanString.TRUE
            )
          );
        }

        // Filter for templates if on the templates route
        if (this.routeUrl === Routes.DOCUMENTS_TEMPLATES && folderChildren) {
          folderChildren = await this.filterTemplateVisibleChildren(folderChildren);
        }
      }

      // Update the main data array with newly uploaded files
      const currentExplorerAttrs = this.explorerAttributes();
      const existingEntities = filterBizEntitiesForApplicationExplorer(currentExplorerAttrs.data);
      const newlyUploadedEntities = filterBizEntitiesForApplicationExplorer(folderChildren);

      // Add newly uploaded files to the main data array if they don't already exist
      if (newlyUploadedEntities) {
        const mergedEntities = [...existingEntities];
        for (const uploadedEntity of newlyUploadedEntities) {
          const alreadyExists = mergedEntities.some(entity => entity.id === uploadedEntity.id);
          if (!alreadyExists) {
            mergedEntities.push(uploadedEntity);
            this.logger.info(`✅ Added newly uploaded file to data array: ${uploadedEntity.name} (${uploadedEntity.id})`);
          }
        }

        // Update the explorer attributes with the merged data
        const updatedExplorerAttrs = {
          ...currentExplorerAttrs,
          data: mergedEntities
        };
        this.explorerState.explorerAttributes.set(updatedExplorerAttrs);
      }

      this.explorerState.updateAfterUpload(
        event.folderId,
        newlyUploadedEntities,
        (info) => this.onContextMenuClicked(info),
        (id) => this.onItemSelected(id),
        (id) => this.onListItemClicked(id),
        (entity) => this.handleItemDoubleClick(entity)
      );
      this.explorerState.refreshExplorer();
    } catch (error) {
      this.logger.error(`❌ Error refreshing folder contents: ${error}`);
      this.toastService.toast('Files uploaded successfully, but failed to refresh the view. Please refresh the page.');
    } finally {
      this.isLoading.set(false);
    }
  }

  onUploadStarted(): void {
    this.isLoading.set(true);
  }

  /**
   * Handle upload cancellation
   */
  onUploadCancelled(): void {
    this.isLoading.set(false);
    this.showUploadDialog.set(false);
  }

  /**
   * Handle new file creation via off-canvas
   */
  handleNewFileCreationByOffCanvas(item?: IBizEntity | any, event?: any): void {
    this.logger.info(`🔧 handleNewFileCreationByOffCanvas called for ${item?.name || 'unknown'}`);

    if (!item) {
      this.logger.error('❌ No file type item provided');
      return;
    }

    // Store the item context for later use when the form is submitted
    this.currentFileItem.set(item);

    // Set the data in the off-canvas service BEFORE opening
    this.offCanvasFormService.dataOfCurrentFormInView = item;

    // Open the off-canvas with the file type data
    this.logger.info(`✅ Opening off-canvas for file creation. Item: ${item.name}, Category: ${item.category}`);
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      OffCanvasComponents.AddFile,
      OffCanvasIDsInDefaultLayout.l1_id,
      1
    );
  }


  openStrategyExplorer = (item: IBizEntityType) => {
    let modalConfig = GetDefaultConfig()
    modalConfig.title = 'Select Template';
    modalConfig.dialog = getExplorerPropertiesBasedOnBizEntityType(item)?.modalChooser!;
    modalConfig.size = ModalDialogSizes.XL;

    // Store the category to be passed to the modal
    modalConfig.model = {
      in: {
        ...modalConfig.model?.in,
        modalCategory: item.category // Pass the category to the modal
      },
      out: modalConfig.model?.out || {}
    };

    modalConfig.footerButtons.push({
      id: ModalButtonIDs.BizEntity_Select,
      label: 'Confirm Selection',
      color: 'primary',
      click: async (data: any) => {
        const file = await this.entityFileUrlService.getFileUrl(data?.id || '');
        this.logger.info(`📄 Template selected: ${data?.name}, File URL: ${file?.fileUrl[0]}`);

        // Store the template data in global service for the form to access
        this.global.setSelectedTemplateData(
          data?.id || '',
          file?.fileUrl[0] || '',
          data?.name || ''
        );

        // Set the data in the off-canvas service (use item type, not entity)
        this.offCanvasFormService.dataOfCurrentFormInView = item;

        // Open the off-canvas form to get the file name
        this.logger.info(`✅ Opening off-canvas for file creation with template`);
        openOffCanvas(
          this.offCanvasFormService,
          this.offCanvasServiceRef,
          OffCanvasComponents.AddFile,
          OffCanvasIDsInDefaultLayout.l1_id,
          1
        );
      },
      getData: true,
      formValidate: false,
      closesModal: true,
      isEnabled: (data: IBizEntity) => {
        return true;
      },

    })
    this.modalService.show(modalConfig);
  }



  /**
   * Handle "New from Template" button click
   * This opens the template selector modal for the specific category
   */
  handleNewFromTemplate(item: IBizEntityType, category: BizEntityTypeCategory, event: any): void {
    this.logger.info(`📋 New from Template clicked for category: ${category}, item: ${item.name}`);

    // Store the category for template creation
    this.newTemplateCategory = item.category!;
    this.logger.info(`📋 Set newTemplateCategory: ${this.newTemplateCategory}`);

    // Open the modal with the category passed through config
    this.openStrategyExplorer(item);
  }

  /**
   * Handle new file creation with custom name
   * This is still used for legacy dialog-based creation (diagram, presentation, PDF)
   */
  async handleCustomFileCreation(item: IBizEntity | any, customFileName: string): Promise<void> {
    if (!item || !customFileName) {
      this.toastService.toast('Please provide valid file type and name.');
      return;
    }

    try {
      // Use queryParentId if available from URL parameter, otherwise use selected parent
      const parentId = this.queryParentId || this.explorerState.getSelectedParentId();
      this.logger.info(`🔧 Creating file: ${item.name || 'unknown'}, category: ${item.category}, fileName: ${customFileName}, parent: ${parentId}`);

      const newFile = await this.fileOps.handleNewFileCreation(item, customFileName, parentId);
      this.logger.info(`✅ File created successfully: ${newFile?.name || 'unknown'} (ID: ${newFile?.id || 'unknown'})`);

      if (!newFile || !newFile.id) {
        this.logger.error(`❌ File creation returned invalid result for ${customFileName}`);
        this.toastService.toast('Failed to create file. Please try again.');
        return;
      }

      // Generate thumbnail if markdown
      if (item.category === BizEntityTypeCategory.HayagrivaFileMarkdown) {
        this.logger.info('🖼️ Generating markdown thumbnail...');
        await this.fileOps.generateMarkdownThumbnailForNewFile(
          newFile,
          '# New Document\n\nStart writing your markdown content here...\n'
        );
      }

      // Refresh the current folder to show the new file
      this.logger.info('🔄 Refreshing folder view...');
      await this.refreshCurrentFolder();

      // Open the created file
      this.logger.info('📂 Opening created file in viewer...');
      await this.openCreatedFile(newFile, item.category);

      // Clear queryParentId after use
      this.queryParentId = null;
    } catch (error) {
      this.logger.error(`❌ Failed to create file: ${error}`);

      if (error instanceof Error && error.message === UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD) {
        this.toastService.toast(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
        return;
      }

      this.toastService.toast('Failed to create file. Please try again.');
    }
  }

  /**
   * Open a newly created file in viewer
   */
  private async openCreatedFile(file: IBizEntityWithAccessUrl, category: BizEntityTypeCategory): Promise<void> {
    try {
      // Fetch the complete entity with rights from the API
      const completeEntity = await this.facade.getBizEntityById(file.id);

      if (!completeEntity) {
        this.logger.error(`❌ Failed to fetch complete entity data for ID: ${file.id}`);
        return;
      }

      this.bizEntityId = completeEntity.id;
      this.category = category;
      this.mode = completeEntity.rights as IRights;

      // Set the selected entity for use in menu actions (e.g., Discussion, Save as template)
      this.appStateService.selectedBizEntityForEdit = completeEntity;

      this.updateExplorerForViewer(true);
      this.explorerState.refreshExplorer();
    } catch (error) {
      this.logger.error(`❌ Error opening created file: ${error}`);
    }
  }

  closeUploadDialog(): void {
    this.showUploadDialog.set(false);
  }

  // ==================== Placeholder Methods ====================

  private async handleCreateFolder(): Promise<void> {
    this.logger.info('🔧 Create folder functionality - DIRECT CALL');

    // Directly call openOffCanvas to test
    openOffCanvas(
      this.offCanvasFormService,
      this.offCanvasServiceRef,
      OffCanvasComponents.AddFolder,
      OffCanvasIDsInDefaultLayout.l1_id,
      1
    );

    this.logger.info('✅ openOffCanvas called from handleCreateFolder');
  }

  private handleImportFromUrl(): void {
    // TODO: Implement URL import functionality
    this.logger.warn('⚠️ Import from URL functionality not yet implemented');
  }

  /**
   * Get the currently selected entity
   */
  get data(): IBizEntity | null {
    const selectedEntity = this.appStateService.selectedBizEntityForEdit;
    // Only return if it's a template (for modal use)
    if (this.isInModal && selectedEntity && selectedEntity.additionalInfo && Array.isArray(selectedEntity.additionalInfo)) {
      const isTemplate = selectedEntity.additionalInfo.some((info) =>
        info.fieldMeta?.name?.toLowerCase() === AdditionalFieldNames.IS_TEMPLATE &&
        info.value?.toLowerCase() === BooleanString.TRUE
      );
      return isTemplate ? selectedEntity : null;
    }
    return selectedEntity ? selectedEntity : null;
  }

  private handleSaveFromPreview(entity?: IBizEntity): void {
    const targetEntity = entity || this.appStateService.selectedBizEntityForEdit || undefined;
    const entityId = targetEntity?.id;

    this.logger.info(`💾 Context menu save requested for entity: ${targetEntity?.name || entityId || 'undefined'}`);
    this.global.requestSave(entityId, 'context-menu');
  }

}

export enum ModalButtonIDs {
  Measure_Fields_Save = "measure-fields-save",
  Measure_Fields_Reset = "measure-fields-revert",
  Explorer_Select_Deliverable = "explorer-select-deliverable",
  Role_Field_Save = "role-field-save",
  Team_Field_Save = "team-field-save",
  Message_OK = "message-ok",
  KPI_Select = "kpi-select",
  BizEntity_Select = "bizentity-select",
  BizRule_Save = "bizrule-save",
  Message_Cancel = "message-cancel",
}

